import '../scss/components/_ownercompliance.scss'
import React, { useState, useEffect } from 'react'
import { getAllComplianceByPGID, updateComplianceStatus, getTenantRegisterByAdmission } from '../apis/services/pgApi'
import ViewAgreementModal from './RentAgreement'
import * as XLSX from 'xlsx'

// ─── Constants ────────────────────────────────────────────────────
const POLICE_CFG = {
  COMPLETED: { label: 'Completed', color: '#16A34A', dot: '#22C55E' },
  PENDING: { label: 'Pending', color: '#D97706', dot: '#F59E0B' },
  REJECTED: { label: 'Rejected', color: '#DC2626', dot: '#EF4444' },
  IN_PROGRESS: { label: 'In Progress', color: '#3B82F6', dot: '#60A5FA' },
}

const AGREEMENT_CFG = {
  active: { label: 'Active', bg: '#F0FDF4', text: '#166534', border: '#86EFAC' },
  expired: { label: 'Expired', bg: '#FFF1F2', text: '#991B1B', border: '#FECACA' },
  expiring_soon: { label: 'Expiring Soon', bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
}

const PAGE_SIZE = 5

// ─── Helper Functions ─────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatDateFull = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

// ─── Excel Download Helper ────────────────────────────────────────────
const downloadExcel = (data, fileName) => {
  if (!data || data.length === 0) {
    alert('No data to download')
    return
  }

  try {
    const ws = XLSX.utils.json_to_sheet(data)
    
    const colWidths = []
    const headers = Object.keys(data[0])
    headers.forEach((key, index) => {
      let maxLength = key.length
      data.forEach(row => {
        const value = row[key] !== undefined && row[key] !== null ? String(row[key]) : ''
        if (value.length > maxLength) maxLength = value.length
      })
      colWidths.push({ wch: Math.min(Math.max(maxLength + 4, 12), 40) })
    })
    ws['!cols'] = colWidths

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Tenant Register')
    XLSX.writeFile(wb, `${fileName}.xlsx`)
  } catch (error) {
    console.error('Error downloading Excel:', error)
    alert('Failed to download Excel file')
  }
}

// ─── Tenant Register Modal ─────────────────────────────────────────
const TenantRegisterModal = ({ tenantData, onClose, loading }) => {
  if (!tenantData) return null

  const data = Array.isArray(tenantData) ? tenantData[0] : tenantData

  const handleDownloadSingle = () => {
    if (!data) {
      alert('No data to download')
      return
    }

    const excelData = {
      'Tenant ID': data.TenantId || '—',
      'Full Name': data.FullName || '—',
      'Gender': data.Gender || '—',
      'Date of Birth': data['Date Of Birth'] || '—',
      'Mobile Number': data.MobileNumber || '—',
      'Email Address': data.EmailId || '—',
      'Emergency Contact Name': data.EmergenctContactName || '—',
      'Emergency Contact Number': data.EmergencyContactNumber || '—',
      'PG Name': data.PGName || '—',
      'Room Number': data.RoomNumber || '—',
      'Bed Number': data.BedNumber || '—',
      'Joining Date': data.JoinDate || '—',
      'Notice Date': data.NoticeDate || '—',
      'Vacate Date': data.VacateDate || '—',
      'Exit Date': data.ExitDate || '—'
    }

    const fileName = `Tenant_Register_${data.FullName || 'Tenant'}_${data.RoomNumber || ''}`
    downloadExcel([excelData], fileName)
  }

  return (
    <div className="oc-modal-overlay" onClick={onClose}>
      <div className="oc-modal oc-modal-large" onClick={e => e.stopPropagation()}>
        <div className="oc-modal-head">
          <div>
            <h3 className="oc-modal-title">📋 Tenant Register Details</h3>
            <p className="oc-modal-sub">Complete tenant information</p>
          </div>
          <div className="oc-modal-head-actions">
            {!loading && data && (
              <button 
                className="oc-download-single-btn"
                onClick={handleDownloadSingle}
                title="Download this tenant's register"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                </svg>
                Download
              </button>
            )}
            <button className="oc-modal-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="oc-modal-body oc-modal-body-no-scroll">
          {loading ? (
            <div className="oc-loading">
              <div className="oc-spinner"></div>
              <p>Loading tenant details...</p>
            </div>
          ) : !data ? (
            <div className="oc-modal-empty">
              <p>No tenant data found</p>
            </div>
          ) : (
            <div className="tenant-register-details">
              <div className="tenant-detail-row">
                <div className="tenant-detail-col">
                  <h4 className="tenant-detail-section-title">👤 Personal Information</h4>
                  <div className="tenant-detail-grid">
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Tenant ID</span>
                      <span className="tenant-detail-value">{data.TenantId || '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Full Name</span>
                      <span className="tenant-detail-value">{data.FullName || '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Gender</span>
                      <span className="tenant-detail-value">{data.Gender || '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Date of Birth</span>
                      <span className="tenant-detail-value">{formatDateFull(data['Date Of Birth'])}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Mobile Number</span>
                      <span className="tenant-detail-value">{data.MobileNumber || '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Email Address</span>
                      <span className="tenant-detail-value">{data.EmailId || '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="tenant-detail-col">
                  <h4 className="tenant-detail-section-title">🆘 Emergency Contact</h4>
                  <div className="tenant-detail-grid">
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Contact Name</span>
                      <span className="tenant-detail-value">{data.EmergenctContactName || '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Contact Number</span>
                      <span className="tenant-detail-value">{data.EmergencyContactNumber || '—'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="tenant-detail-row">
                <div className="tenant-detail-col">
                  <h4 className="tenant-detail-section-title">🏢 PG & Room Details</h4>
                  <div className="tenant-detail-grid">
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">PG Name</span>
                      <span className="tenant-detail-value">{data.PGName || '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Room Number</span>
                      <span className="tenant-detail-value">{data.RoomNumber || '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Bed Number</span>
                      <span className="tenant-detail-value">{data.BedNumber || '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="tenant-detail-col">
                  <h4 className="tenant-detail-section-title">📅 Tenancy Dates</h4>
                  <div className="tenant-detail-grid">
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Joining Date</span>
                      <span className="tenant-detail-value">{formatDateFull(data.JoinDate)}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Notice Date</span>
                      <span className="tenant-detail-value">{data.NoticeDate ? formatDateFull(data.NoticeDate) : '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Vacate Date</span>
                      <span className="tenant-detail-value">{data.VacateDate ? formatDateFull(data.VacateDate) : '—'}</span>
                    </div>
                    <div className="tenant-detail-item">
                      <span className="tenant-detail-label">Exit Date</span>
                      <span className="tenant-detail-value">
                        {data.ExitDate ? (
                          formatDateFull(data.ExitDate)
                        ) : (
                          <span className="tenant-status-active">—</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="oc-modal-footer">
          {!loading && data && (
            <button 
              className="oc-download-footer-btn"
              onClick={handleDownloadSingle}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              Download Excel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Record Modal ─────────────────────────────────────────────────
const RecordModal = ({ title, sub, rows, columns, onClose }) => (
  <div className="oc-modal-overlay" onClick={onClose}>
    <div className="oc-modal" onClick={e => e.stopPropagation()}>
      <div className="oc-modal-head">
        <div>
          <h3 className="oc-modal-title">{title}</h3>
          <p className="oc-modal-sub">{sub}</p>
        </div>
        <button className="oc-modal-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      <div className="oc-modal-body">
        {rows.length === 0 ? (
          <p className="oc-modal-empty">No records found.</p>
        ) : (
          <table className="oc-modal-table">
            <thead>
              <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>{columns.map(c => <td key={c.key}>{row[c.key]}</td>)}</tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
)

// ─── Records Table Head ───────────────────────────────────────────
const RecordsHead = () => (
  <div className="oc-rec-head">
    <div className="oc-rc oc-rc-room">Room</div>
    <div className="oc-rc oc-rc-tenant">Tenant Name</div>
    <div className="oc-rc oc-rc-reg">Tenant Register</div>
    <div className="oc-rc oc-rc-police">Police Verification</div>
    <div className="oc-rc oc-rc-checkin">Check-In/Out</div>
    <div className="oc-rc oc-rc-visitors">Visitors</div>
    <div className="oc-rc oc-rc-agreement">Agreement</div>
    <div className="oc-rc oc-rc-download">Download All</div>
  </div>
)

// ─── Pending Requests Tab ─────────────────────────────────────────
const PendingRequests = ({ complianceData, onUpdateStatus, loading }) => {
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(1)

  const pendingRequests = complianceData.filter(item => 
    item.ComplianceType !== 'Police Verification' && 
    item.Status === 'PENDING'
  )

  const getType = (complianceType) => {
    if (complianceType?.toLowerCase().includes('visitor')) return 'visitor'
    if (complianceType?.toLowerCase().includes('check') || complianceType?.toLowerCase().includes('in/out')) return 'checkin'
    return 'other'
  }

  const filtered = pendingRequests.filter(item => {
    if (filter === 'all') return true
    return getType(item.ComplianceType) === filter
  })

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const policeVerificationPending = complianceData.filter(item => 
    item.ComplianceType === 'Police Verification' && 
    item.Status === 'PENDING'
  )

  if (loading) {
    return (
      <div className="oc-root">
        <div className="oc-loading">
          <div className="oc-spinner"></div>
          <p>Loading compliance data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="oc-root">
      <div className="oc-section">
        <div className="oc-section-title-row">
          <h3 className="oc-section-title">Pending Requests</h3>
          <span className="oc-count-badge">{pendingRequests.length}</span>
        </div>

        <div className="oc-filter-pills">
          {[
            ['all', 'All', pendingRequests.length],
            ['visitor', 'Visitor', pendingRequests.filter(i => getType(i.ComplianceType) === 'visitor').length],
            ['checkin', 'Check-In / Check-Out', pendingRequests.filter(i => getType(i.ComplianceType) === 'checkin').length]
          ].map(([key, label, count]) => (
            <button 
              key={key} 
              className={`oc-filter-pill ${filter === key ? 'active' : ''}`}
              onClick={() => { setFilter(key); setPage(1) }}
            >
              {label}
              {count > 0 && <span className="oc-pill-count">{count}</span>}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="oc-table-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p>No pending requests</p>
          </div>
        ) : (
          <div className="oc-table">
            <div className="oc-table-head">
              <div className="oc-col oc-col-room">Room</div>
              <div className="oc-col oc-col-tenant">Tenant</div>
              <div className="oc-col oc-col-type">Type</div>
              <div className="oc-col oc-col-details">Details</div>
              <div className="oc-col oc-col-date">Date Range</div>
              <div className="oc-col oc-col-actions">Actions</div>
            </div>
            {paged.map(item => (
              <div key={item.ComplianceId} className="oc-table-row">
                <div className="oc-col oc-col-room">{item.RoomNumber || '—'}</div>
                <div className="oc-col oc-col-tenant">
                  <strong>{item.tenantName}</strong>
                </div>
                <div className="oc-col oc-col-type">
                  <span className={`oc-type-badge oc-type-${getType(item.ComplianceType)}`}>
                    {getType(item.ComplianceType) === 'visitor'
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
                    }
                    {getType(item.ComplianceType) === 'visitor' ? 'Visitor' : 'Check-In/Out'}
                  </span>
                </div>
                <div className="oc-col oc-col-details">
                  <span className="oc-detail-main">{item.ComplianceDetails || '—'}</span>
                </div>
                <div className="oc-col oc-col-date">{item.DateRange || formatDate(item.CreatedDate)}</div>
                <div className="oc-col oc-col-actions">
                  <button 
                    className="oc-btn oc-btn-approve" 
                    onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
                  >
                    Approve
                  </button>
                  <button 
                    className="oc-btn-text-reject" 
                    onClick={() => onUpdateStatus(item.ComplianceId, 'REJECTED')}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="oc-pagination">
            <span className="oc-page-info">Page {page} of {totalPages}</span>
            <div className="oc-page-btns">
              <button className="oc-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="2"/></svg>
              </button>
              <button className="oc-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2"/></svg>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="oc-section oc-police-verification">
        <div className="oc-section-title-row">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <h3 className="oc-section-title">Police Verification Pending</h3>
          <span className="oc-count-badge oc-count-amber">{policeVerificationPending.length}</span>
        </div>
        
        {policeVerificationPending.length === 0 ? (
          <div className="oc-table-empty">
            <p>No police verification pending</p>
          </div>
        ) : (
          <div className="oc-table">
            <div className="oc-table-head">
              <div className="oc-col oc-col-room">Room</div>
              <div className="oc-col oc-col-tenant">Tenant Name</div>
              <div className="oc-col oc-col-pv-status">Status</div>
              <div className="oc-col oc-col-actions">Actions</div>
            </div>
            {policeVerificationPending.map(item => (
              <div key={item.ComplianceId} className="oc-table-row">
                <div className="oc-col oc-col-room">{item.RoomNo || '—'}</div>
                <div className="oc-col oc-col-tenant">
                  <strong>{item.TenantName || `Tenant #${item.TenantID}`}</strong>
                </div>
                <div className="oc-col oc-col-pv-status">
                  <span className={`oc-pv-badge oc-pv-${item.Status?.toLowerCase()}`}>
                    {item.Status?.charAt(0).toUpperCase() + item.Status?.slice(1).toLowerCase()}
                  </span>
                </div>
                <div className="oc-col oc-col-actions">
                  <button className="oc-btn-reminder">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    Send Reminder
                  </button>
                  <button 
                    className="oc-btn oc-btn-approve" 
                    onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2"/></svg>
                    Mark Verified
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Tenant Record Row ────────────────────────────────────────────
const TenantRecordRow = ({ item, onViewAgreement, onViewTenantRegister }) => {
  const [modal, setModal] = useState(null)
  const ps = POLICE_CFG[item.Status] || POLICE_CFG.PENDING
  const agreementStatus = item.Status === 'COMPLETED' ? 'active' : 'expired'
  const agreementConfig = AGREEMENT_CFG[agreementStatus]

  return (
    <>
      <div className="oc-rec-row">
        <div className="oc-rc oc-rc-room">{item.RoomNumber || '—'}</div>
        <div className="oc-rc oc-rc-tenant"><strong>{item.tenantName || `Tenant #${item.tenantId}`}</strong></div>
        <div className="oc-rc oc-rc-reg">
          <button 
            className="oc-link-btn"
            onClick={() => onViewTenantRegister(item)}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            View
          </button>
        </div>
        <div className="oc-rc oc-rc-police">
          <span className="oc-police-label" style={{ color: ps.color }}>
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              {item.Status === 'COMPLETED'
                ? <path d="M2 6L5 9L10 3" stroke={ps.dot} strokeWidth="2" strokeLinecap="round"/>
                : <circle cx="6" cy="6" r="5" stroke={ps.dot} strokeWidth="1.5"/>}
            </svg>
            {ps.label}
          </span>
          <button className="oc-icon-btn" title="Download">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          </button>
        </div>
        <div className="oc-rc oc-rc-checkin">
          <button className="oc-link-btn" onClick={() => setModal('checkin')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            0 records
          </button>
        </div>
        <div className="oc-rc oc-rc-visitors">
          <button className="oc-link-btn" onClick={() => setModal('visitors')}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            0 visitors
          </button>
        </div>
        <div className="oc-rc oc-rc-agreement">
          <span className="oc-agreement-badge" style={{ background: agreementConfig.bg, color: agreementConfig.text, borderColor: agreementConfig.border }}>
            {agreementConfig.label}
          </span>
          <button 
            className="oc-icon-btn" 
            title="View agreement"
            onClick={() => onViewAgreement(item)}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
        </div>
        <div className="oc-rc oc-rc-download">
          <button className="oc-download-btn">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
            Download All
          </button>
        </div>
      </div>

      {modal === 'checkin' && (
        <RecordModal
          title="Check-In / Check-Out Records"
          sub={`${item.tenantName} · Room ${item.RoomNumber}`}
          rows={item.CheckIns || []}
          columns={[{ key: 'date', label: 'Date' }, { key: 'reason', label: 'Reason' }]}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'visitors' && (
        <RecordModal
          title="Visitor Records"
          sub={`${item.tenantName} · Room ${item.RoomNumber}`}
          rows={item.Visitors || []}
          columns={[{ key: 'name', label: 'Visitor Name' }, { key: 'relation', label: 'Relation' }, { key: 'date', label: 'Date' }]}
          onClose={() => setModal(null)}
        />
      )}
    </>
  )
}

// ─── Tenant Records Tab ───────────────────────────────────────────
// const TenantRecords = ({ complianceData, loading, onViewAgreement, onViewTenantRegister, selectedPGId }) => {
//   const [search, setSearch] = useState({ mobile: '', room: '', tenant: '' })
//   const [dateFrom, setDateFrom] = useState('')
//   const [dateTo, setDateTo] = useState('')
//   const [statusFilter, setStatusFilter] = useState('all')
//   const [openMonths, setOpenMonths] = useState({})
//   const [downloadingAll, setDownloadingAll] = useState(false)

//   const activeRecords = complianceData.filter(item => item.Status === 'COMPLETED')
//   const previousRecords = complianceData.filter(item => item.Status === 'REJECTED')

//   const applySearch = (records) => {
//     return records.filter(item => {
//       const ms = !search.mobile || (item.TenantName || '').toLowerCase().includes(search.mobile.toLowerCase())
//       const rs = !search.room || (item.RoomNo || '').includes(search.room)
//       const ts = !search.tenant || (item.TenantName || '').toLowerCase().includes(search.tenant.toLowerCase())
      
//       if (dateFrom && item.CreatedDate) {
//         const itemDate = new Date(item.CreatedDate)
//         const fromDate = new Date(dateFrom)
//         if (itemDate < fromDate) return false
//       }
//       if (dateTo && item.CreatedDate) {
//         const itemDate = new Date(item.CreatedDate)
//         const toDate = new Date(dateTo)
//         if (itemDate > toDate) return false
//       }
      
//       return ms && rs && ts
//     })
//   }

//   const filteredActive = applySearch(activeRecords)
//   const filteredPrevious = applySearch(previousRecords)

//   const toggleMonth = (month) => setOpenMonths(p => ({ ...p, [month]: !p[month] }))

//   const byMonth = {}
//   filteredPrevious.forEach(item => {
//     const month = new Date(item.CreatedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
//     if (!byMonth[month]) byMonth[month] = []
//     byMonth[month].push(item)
//   })
//   const sortedMonths = Object.keys(byMonth).sort((a, b) => new Date(b) - new Date(a))

//   // Handle Download All Tenant Register
//   const handleDownloadAll = async () => {
//     if (!selectedPGId) {
//       alert('No PG selected')
//       return
//     }

//     setDownloadingAll(true)
//     try {
//       const today = new Date()
//       const fromDate = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
//         .toISOString().split('T')[0]
//       const toDate = today.toISOString().split('T')[0]

//       const response = await getTenantRegisterByAdmission(selectedPGId, null, fromDate, toDate)
      
//       if (response.success && response.tenants && response.tenants.length > 0) {
//         const excelData = response.tenants.map(item => ({
//           'Tenant ID': item.TenantId || '—',
//           'Full Name': item.FullName || '—',
//           'Gender': item.Gender || '—',
//           'Date of Birth': item['Date Of Birth'] || '—',
//           'Mobile Number': item.MobileNumber || '—',
//           'Email Address': item.EmailId || '—',
//           'Emergency Contact Name': item.EmergenctContactName || '—',
//           'Emergency Contact Number': item.EmergencyContactNumber || '—',
//           'PG Name': item.PGName || '—',
//           'Room Number': item.RoomNumber || '—',
//           'Bed Number': item.BedNumber || '—',
//           'Joining Date': item.JoinDate || '—',
//           'Notice Date': item.NoticeDate || '—',
//           'Vacate Date': item.VacateDate || '—',
//           'Exit Date': item.ExitDate || '—'
//         }))

//         const pgName = response.tenants[0]?.PGName || 'PG'
//         const fileName = `All_Tenant_Register_${pgName}_${today.toISOString().split('T')[0]}`
//         downloadExcel(excelData, fileName)
//       } else {
//         alert('No tenant data found to download')
//       }
//     } catch (error) {
//       console.error('Error downloading all tenant register:', error)
//       alert('Failed to download tenant register data')
//     } finally {
//       setDownloadingAll(false)
//     }
//   }

//   if (loading) {
//     return (
//       <div className="oc-root">
//         <div className="oc-loading">
//           <div className="oc-spinner"></div>
//           <p>Loading compliance data...</p>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="oc-root">
//       <div className="oc-filter-bar">
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Mobile</label>
//           <input className="oc-filter-input" placeholder="Search mobile..." value={search.mobile}
//             onChange={e => setSearch(p => ({ ...p, mobile: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Room</label>
//           <input className="oc-filter-input" placeholder="Room no." value={search.room}
//             onChange={e => setSearch(p => ({ ...p, room: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Tenant</label>
//           <input className="oc-filter-input" placeholder="Name..." value={search.tenant}
//             onChange={e => setSearch(p => ({ ...p, tenant: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">From</label>
//           <div className="oc-date-wrap">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg>
//             <input type="date" className="oc-filter-input oc-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">To</label>
//           <div className="oc-date-wrap">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg>
//             <input type="date" className="oc-filter-input oc-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Status</label>
//           <select className="oc-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
//             <option value="all">All</option>
//             <option value="active">Active</option>
//             <option value="previous">Previous</option>
//           </select>
//         </div>

//         {/* ✅ Download All Button */}
//         <button 
//           className="oc-download-all-btn"
//           onClick={handleDownloadAll}
//           disabled={downloadingAll}
//         >
//           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//             <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
//           </svg>
//           {downloadingAll ? 'Downloading...' : 'Download All Tenant Register'}
//         </button>
//       </div>

//       {(statusFilter === 'all' || statusFilter === 'active') && (
//         <div className="oc-section">
//           <div className="oc-section-title-row">
//             <h3 className="oc-section-title">Active Tenants</h3>
//             <span className="oc-count-badge">{filteredActive.length}</span>
//           </div>
//           {filteredActive.length === 0 ? (
//             <div className="oc-table-empty">
//               <p>No active tenants found</p>
//             </div>
//           ) : (
//             <div className="oc-records-wrap">
//               <RecordsHead />
//               {filteredActive.map(item => (
//                 <TenantRecordRow 
//                   key={item.ComplianceId} 
//                   item={item} 
//                   onViewAgreement={onViewAgreement}
//                   onViewTenantRegister={onViewTenantRegister}
//                 />
//               ))}
//             </div>
//           )}
//         </div>
//       )}

//       {(statusFilter === 'all' || statusFilter === 'previous') && filteredPrevious.length > 0 && (
//         <div className="oc-section">
//           <div className="oc-section-title-row">
//             <h3 className="oc-section-title">Previous Tenants</h3>
//             <span className="oc-count-badge oc-count-muted">{filteredPrevious.length}</span>
//           </div>
//           <div className="oc-accordion">
//             {sortedMonths.map(month => {
//               const items = byMonth[month]
//               const isOpen = openMonths[month]
//               return (
//                 <div key={month} className="oc-accordion-item">
//                   <button className="oc-accordion-head" onClick={() => toggleMonth(month)}>
//                     <div className="oc-accordion-title">
//                       <span className="oc-accordion-month">{month}</span>
//                       <span className="oc-accordion-count">{items.length} tenant{items.length !== 1 ? 's' : ''}</span>
//                     </div>
//                     <svg className={`oc-chevron ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
//                       <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
//                     </svg>
//                   </button>
//                   {isOpen && (
//                     <div className="oc-accordion-body">
//                       <RecordsHead />
//                       {items.map(item => (
//                         <TenantRecordRow 
//                           key={item.ComplianceId} 
//                           item={item} 
//                           onViewAgreement={onViewAgreement}
//                           onViewTenantRegister={onViewTenantRegister}
//                         />
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )
//             })}
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }

// ─── Tenant Records Tab ───────────────────────────────────────────
const TenantRecords = ({ complianceData, loading, onViewAgreement, onViewTenantRegister, selectedPGId }) => {
  const [search, setSearch] = useState({ mobile: '', room: '', tenant: '' })
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [openMonths, setOpenMonths] = useState({})
  const [downloadingAll, setDownloadingAll] = useState(false)

  const activeRecords = complianceData.filter(item => item.Status === 'COMPLETED')
  const previousRecords = complianceData.filter(item => item.Status === 'REJECTED')

  const applySearch = (records) => {
    return records.filter(item => {
      const ms = !search.mobile || (item.TenantName || '').toLowerCase().includes(search.mobile.toLowerCase())
      const rs = !search.room || (item.RoomNo || '').includes(search.room)
      const ts = !search.tenant || (item.TenantName || '').toLowerCase().includes(search.tenant.toLowerCase())
      
      if (dateFrom && item.CreatedDate) {
        const itemDate = new Date(item.CreatedDate)
        const fromDate = new Date(dateFrom)
        if (itemDate < fromDate) return false
      }
      if (dateTo && item.CreatedDate) {
        const itemDate = new Date(item.CreatedDate)
        const toDate = new Date(dateTo)
        if (itemDate > toDate) return false
      }
      
      return ms && rs && ts
    })
  }

  const filteredActive = applySearch(activeRecords)
  const filteredPrevious = applySearch(previousRecords)

  const toggleMonth = (month) => setOpenMonths(p => ({ ...p, [month]: !p[month] }))

  const byMonth = {}
  filteredPrevious.forEach(item => {
    const month = new Date(item.CreatedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    if (!byMonth[month]) byMonth[month] = []
    byMonth[month].push(item)
  })
  const sortedMonths = Object.keys(byMonth).sort((a, b) => new Date(b) - new Date(a))

  // Handle Download All Tenant Register
  const handleDownloadAll = async () => {
    if (!selectedPGId) {
      alert('No PG selected')
      return
    }

    setDownloadingAll(true)
    try {
      const today = new Date()
      const fromDate = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
        .toISOString().split('T')[0]
      const toDate = today.toISOString().split('T')[0]

      const response = await getTenantRegisterByAdmission(selectedPGId, null, fromDate, toDate)
      
      console.log('Download All Response:', response)
      
      // ✅ Check both 'data' and 'tenants' keys in response
      let tenantsData = []
      if (response.success) {
        // Check if data exists in response.data or response.tenants
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          tenantsData = response.data
        } else if (response.tenants && Array.isArray(response.tenants) && response.tenants.length > 0) {
          tenantsData = response.tenants
        }
      }
      
      console.log('Tenants Data:', tenantsData)
      
      if (tenantsData.length === 0) {
        alert('No tenant data found to download')
        setDownloadingAll(false)
        return
      }

      // ✅ Get distinct tenants by AdmissionId
      const distinctMap = new Map()
      tenantsData.forEach(item => {
        const admissionId = item.AdmissionId || item.AdmissionID
        if (admissionId && !distinctMap.has(admissionId)) {
          distinctMap.set(admissionId, item)
        }
      })
      
      const distinctTenants = Array.from(distinctMap.values())
      console.log('Distinct Tenants:', distinctTenants)

      // Format data for Excel
      const excelData = distinctTenants.map(item => ({
        'Tenant ID': item.TenantId || '—',
        'Admission ID': item.AdmissionId || item.AdmissionID || '—',
        'Full Name': item.FullName || '—',
        'Gender': item.Gender || '—',
        'Date of Birth': item['Date Of Birth'] || item.DateOfBirth || '—',
        'Mobile Number': item.MobileNumber || '—',
        'Email Address': item.EmailId || '—',
        'Emergency Contact Name': item.EmergenctContactName || '—',
        'Emergency Contact Number': item.EmergencyContactNumber || '—',
        'PG Name': item.PGName || '—',
        'Room Number': item.RoomNumber || '—',
        'Bed Number': item.BedNumber || '—',
        'Joining Date': item.JoinDate || '—',
        'Notice Date': item.NoticeDate || '—',
        'Vacate Date': item.VacateDate || '—',
        'Exit Date': item.ExitDate || '—'
      }))

      const pgName = distinctTenants[0]?.PGName || 'PG'
      const fileName = `All_Tenant_Register_${pgName}_${today.toISOString().split('T')[0]}`
      downloadExcel(excelData, fileName)
      
    } catch (error) {
      console.error('Error downloading all tenant register:', error)
      alert('Failed to download tenant register data: ' + (error.message || 'Unknown error'))
    } finally {
      setDownloadingAll(false)
    }
  }

  if (loading) {
    return (
      <div className="oc-root">
        <div className="oc-loading">
          <div className="oc-spinner"></div>
          <p>Loading compliance data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="oc-root">
      <div className="oc-filter-bar">
        <div className="oc-filter-group">
          <label className="oc-filter-label">Mobile</label>
          <input className="oc-filter-input" placeholder="Search mobile..." value={search.mobile}
            onChange={e => setSearch(p => ({ ...p, mobile: e.target.value }))} />
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">Room</label>
          <input className="oc-filter-input" placeholder="Room no." value={search.room}
            onChange={e => setSearch(p => ({ ...p, room: e.target.value }))} />
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">Tenant</label>
          <input className="oc-filter-input" placeholder="Name..." value={search.tenant}
            onChange={e => setSearch(p => ({ ...p, tenant: e.target.value }))} />
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">From</label>
          <div className="oc-date-wrap">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <input type="date" className="oc-filter-input oc-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">To</label>
          <div className="oc-date-wrap">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <input type="date" className="oc-filter-input oc-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
        </div>
        <div className="oc-filter-group">
          <label className="oc-filter-label">Status</label>
          <select className="oc-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="previous">Previous</option>
          </select>
        </div>

        {/* Download All Button */}
        <button 
          className="oc-download-all-btn"
          onClick={handleDownloadAll}
          disabled={downloadingAll}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
          </svg>
          {downloadingAll ? 'Downloading...' : 'Download All'}
        </button>
      </div>

      {(statusFilter === 'all' || statusFilter === 'active') && (
        <div className="oc-section">
          <div className="oc-section-title-row">
            <h3 className="oc-section-title">Active Tenants</h3>
            <span className="oc-count-badge">{filteredActive.length}</span>
          </div>
          {filteredActive.length === 0 ? (
            <div className="oc-table-empty">
              <p>No active tenants found</p>
            </div>
          ) : (
            <div className="oc-records-wrap">
              <RecordsHead />
              {filteredActive.map(item => (
                <TenantRecordRow 
                  key={item.ComplianceId} 
                  item={item} 
                  onViewAgreement={onViewAgreement}
                  onViewTenantRegister={onViewTenantRegister}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {(statusFilter === 'all' || statusFilter === 'previous') && filteredPrevious.length > 0 && (
        <div className="oc-section">
          <div className="oc-section-title-row">
            <h3 className="oc-section-title">Previous Tenants</h3>
            <span className="oc-count-badge oc-count-muted">{filteredPrevious.length}</span>
          </div>
          <div className="oc-accordion">
            {sortedMonths.map(month => {
              const items = byMonth[month]
              const isOpen = openMonths[month]
              return (
                <div key={month} className="oc-accordion-item">
                  <button className="oc-accordion-head" onClick={() => toggleMonth(month)}>
                    <div className="oc-accordion-title">
                      <span className="oc-accordion-month">{month}</span>
                      <span className="oc-accordion-count">{items.length} tenant{items.length !== 1 ? 's' : ''}</span>
                    </div>
                    <svg className={`oc-chevron ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="oc-accordion-body">
                      <RecordsHead />
                      {items.map(item => (
                        <TenantRecordRow 
                          key={item.ComplianceId} 
                          item={item} 
                          onViewAgreement={onViewAgreement}
                          onViewTenantRegister={onViewTenantRegister}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────
const OwnerCompliance = ({ selectedPG, selectedPGId }) => {
  const [activeTab, setActiveTab] = useState('pending')
  const [complianceData, setComplianceData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showAgreement, setShowAgreement] = useState(false)
  const [agreementFileName, setAgreementFileName] = useState('')
  
  const [showTenantRegister, setShowTenantRegister] = useState(false)
  const [tenantRegisterData, setTenantRegisterData] = useState(null)
  const [tenantRegisterLoading, setTenantRegisterLoading] = useState(false)

  useEffect(() => {
    if (selectedPGId) {
      fetchComplianceData()
    }
  }, [selectedPGId])

  const fetchComplianceData = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getAllComplianceByPGID(selectedPGId)
      if (response && response.length > 0) {
        setComplianceData(response)
      } else {
        setComplianceData([])
      }
    } catch (err) {
      console.error('Error fetching compliance data:', err)
      setError('Failed to load compliance data')
      setComplianceData([])
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStatus = async (complianceId, newStatus) => {
    console.log("Updating compliance status:", { complianceId, newStatus });
    
    try {
      const response = await updateComplianceStatus({
        ComplianceID: parseInt(complianceId),
        Status: newStatus
      })
      console.log("response from updateComplianceStatus:", response);
      
      if (response.Success === 1) {
        await fetchComplianceData()
        alert('Status updated successfully!')
      } else {
        alert(response.Message || 'Failed to update status')
      }
    } catch (err) {
      console.error('Error updating status:', err)
      alert('Failed to update status')
    }
  }

  const handleViewAgreement = (item) => {
    const fileName = `Rent_Agreement_Room-${item.RoomNumber}_${(item.tenantName || 'Tenant').replace(/\s+/g, '_')}.pdf`
    setAgreementFileName(fileName)
    setShowAgreement(true)
  }

  const handleViewTenantRegister = async (item) => {
    const admissionId = item.AdmissionID || item.AdmissionId
    
    if (!admissionId) {
      alert('No Admission ID found for this tenant')
      return
    }

    setShowTenantRegister(true)
    setTenantRegisterLoading(true)
    setTenantRegisterData(null)

    try {
      const today = new Date()
      const fromDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
        .toISOString().split('T')[0]
      const toDate = today.toISOString().split('T')[0]
      
      const pgId = selectedPGId

      console.log('Fetching tenant register with:', { pgId, admissionId, fromDate, toDate })
      
      const response = await getTenantRegisterByAdmission(pgId, admissionId, fromDate, toDate)
      console.log('Tenant Register Response:', response)
      
      if (response.success) {
        setTenantRegisterData(response.tenants || response.data || response)
      } else {
        alert(response.message || 'Failed to fetch tenant details')
        setShowTenantRegister(false)
      }
    } catch (err) {
      console.error('Error fetching tenant register:', err)
      alert('Failed to fetch tenant details')
      setShowTenantRegister(false)
    } finally {
      setTenantRegisterLoading(false)
    }
  }

  if (error) {
    return (
      <div className="ocomp-root">
        <div className="oc-error">
          <p>{error}</p>
          <button onClick={fetchComplianceData} className="oc-retry-btn">Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className="ocomp-root">
      <div className="ocomp-tabs">
        <button className={`ocomp-tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          Pending Requests
        </button>
        <button className={`ocomp-tab ${activeTab === 'records' ? 'active' : ''}`} onClick={() => setActiveTab('records')}>
          Tenant Compliance Records
        </button>
      </div>
      
      {activeTab === 'pending' && (
        <PendingRequests 
          complianceData={complianceData}
          onUpdateStatus={handleUpdateStatus}
          loading={loading}
        />
      )}
      
      {activeTab === 'records' && (
        <TenantRecords 
          complianceData={complianceData}
          loading={loading}
          onViewAgreement={handleViewAgreement}
          onViewTenantRegister={handleViewTenantRegister}
          selectedPGId={selectedPGId}
        />
      )}

      {showAgreement && (
        <ViewAgreementModal
          fileName={agreementFileName}
          onClose={() => setShowAgreement(false)}
        />
      )}

      {showTenantRegister && (
        <TenantRegisterModal
          tenantData={tenantRegisterData}
          loading={tenantRegisterLoading}
          onClose={() => {
            setShowTenantRegister(false)
            setTenantRegisterData(null)
          }}
        />
      )}

      <style>{`
        .oc-modal-large {
          max-width: 900px !important;
          width: 90% !important;
          max-height: 90vh !important;
        }

        .oc-modal-body-no-scroll {
          overflow: visible !important;
          padding: 20px 24px !important;
          max-height: none !important;
        }

        .tenant-register-details {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .tenant-detail-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          padding: 12px 0;
          border-bottom: 1px solid #F3F4F6;
        }

        .tenant-detail-row:last-child {
          border-bottom: none;
        }

        .tenant-detail-col {
          display: flex;
          flex-direction: column;
        }

        .tenant-detail-section-title {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 12px 0;
          padding-bottom: 6px;
          border-bottom: 2px solid #6366F1;
          display: inline-block;
          width: auto;
        }

        .tenant-detail-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 6px 0;
        }

        .tenant-detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 0;
          border-bottom: 1px solid #F9FAFB;
        }

        .tenant-detail-item:last-child {
          border-bottom: none;
        }

        .tenant-detail-label {
          font-size: 12px;
          font-weight: 500;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .tenant-detail-value {
          font-size: 13px;
          color: #111827;
          font-weight: 500;
          text-align: right;
          max-width: 60%;
          word-break: break-word;
        }

        .tenant-status-active {
          color: #16A34A;
          font-weight: 600;
        }

        .oc-modal-empty {
          text-align: center;
          padding: 40px 20px;
          color: #6B7280;
        }

        /* Download Button Styles */
        .oc-modal-head-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .oc-download-single-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: #6366F1;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .oc-download-single-btn:hover {
          background: #4F46E5;
          transform: scale(1.02);
        }

        .oc-download-single-btn svg {
          width: 14px;
          height: 14px;
        }

        .oc-modal-footer {
          padding: 12px 24px 20px 24px;
          border-top: 1px solid #E5E7EB;
          display: flex;
          justify-content: flex-end;
        }

        .oc-download-footer-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: #6366F1;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .oc-download-footer-btn:hover {
          background: #4F46E5;
        }

        .oc-download-footer-btn svg {
          width: 16px;
          height: 16px;
        }

        /* Download All Button in Filter Bar */
        .oc-download-all-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #f06e18;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .oc-download-all-btn:hover:not(:disabled) {
          background: #4F46E5;
          transform: scale(1.02);
        }

        .oc-download-all-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .oc-download-all-btn svg {
          width: 16px;
          height: 16px;
        }

        /* Filter bar adjustments */
        .oc-filter-bar {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          gap: 7px;
          padding: 16px 20px;
          background: white;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
          margin-bottom: 20px;
        }

        .oc-filter-bar .oc-filter-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 0 1 auto;
          min-width: 120px;
        }

        .oc-filter-bar .oc-filter-group .oc-filter-label {
          font-size: 11px;
          font-weight: 500;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .oc-filter-bar .oc-filter-group .oc-filter-input,
        .oc-filter-bar .oc-filter-group .oc-filter-select {
          padding: 6px 10px;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          font-size: 13px;
          background: white;
          min-width: 130px;
          height: 36px;
        }

        .oc-filter-bar .oc-filter-group .oc-filter-input:focus,
        .oc-filter-bar .oc-filter-group .oc-filter-select:focus {
          outline: none;
          border-color: #6366F1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        @media (max-width: 768px) {
          .tenant-detail-row {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .tenant-detail-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          }

          .tenant-detail-value {
            text-align: left;
            max-width: 100%;
          }

          .oc-modal-large {
            width: 95% !important;
            max-height: 95vh !important;
          }

          .oc-modal-body-no-scroll {
            padding: 16px !important;
          }

          .oc-filter-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .oc-filter-bar .oc-filter-group {
            min-width: 100%;
          }

          .oc-download-all-btn {
            justify-content: center;
            margin-top: 8px;
            width: 100%;
          }

          .oc-modal-head-actions {
            flex-direction: column;
            align-items: flex-end;
          }

          .oc-download-single-btn {
            font-size: 12px;
            padding: 4px 12px;
          }

          .oc-modal-footer {
            justify-content: center;
          }

          .oc-download-footer-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .tenant-detail-section-title {
            font-size: 12px;
          }

          .tenant-detail-label {
            font-size: 10px;
          }

          .tenant-detail-value {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  )
}

export default OwnerCompliance

// import '../scss/components/_ownercompliance.scss'
// import React, { useState, useEffect } from 'react'
// import { getAllComplianceByPGID, updateComplianceStatus, getTenantRegisterByAdmission } from '../apis/services/pgApi'
// import ViewAgreementModal from './RentAgreement'
// import agreementData from '../json/rentagreementdata.json'

// // ─── Constants ────────────────────────────────────────────────────
// const POLICE_CFG = {
//   COMPLETED: { label: 'Completed', color: '#16A34A', dot: '#22C55E' },
//   PENDING: { label: 'Pending', color: '#D97706', dot: '#F59E0B' },
//   REJECTED: { label: 'Rejected', color: '#DC2626', dot: '#EF4444' },
//   IN_PROGRESS: { label: 'In Progress', color: '#3B82F6', dot: '#60A5FA' },
// }

// const AGREEMENT_CFG = {
//   active: { label: 'Active', bg: '#F0FDF4', text: '#166534', border: '#86EFAC' },
//   expired: { label: 'Expired', bg: '#FFF1F2', text: '#991B1B', border: '#FECACA' },
//   expiring_soon: { label: 'Expiring Soon', bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
// }

// const PAGE_SIZE = 5

// // ─── Helper Functions ─────────────────────────────────────────────────
// const formatDate = (dateString) => {
//   if (!dateString) return '—'
//   const date = new Date(dateString)
//   return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
// }

// const formatDateFull = (dateString) => {
//   if (!dateString) return '—'
//   const date = new Date(dateString)
//   return date.toLocaleDateString('en-US', { 
//     weekday: 'long',
//     year: 'numeric', 
//     month: 'long', 
//     day: 'numeric' 
//   })
// }

// // ─── Tenant Register Modal ─────────────────────────────────────────
// // const TenantRegisterModal = ({ tenantData, onClose, loading }) => {
// //   if (!tenantData) return null

// //   // Check if we have data (could be array or single object)
// //   const data = Array.isArray(tenantData) ? tenantData[0] : tenantData

// //   return (
// //     <div className="oc-modal-overlay" onClick={onClose}>
// //       <div className="oc-modal oc-modal-large" onClick={e => e.stopPropagation()}>
// //         <div className="oc-modal-head">
// //           <div>
// //             <h3 className="oc-modal-title">📋 Tenant Register Details</h3>
// //             <p className="oc-modal-sub">Complete tenant information</p>
// //           </div>
// //           <button className="oc-modal-close" onClick={onClose}>
// //             <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
// //               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
// //             </svg>
// //           </button>
// //         </div>
// //         <div className="oc-modal-body">
// //           {loading ? (
// //             <div className="oc-loading">
// //               <div className="oc-spinner"></div>
// //               <p>Loading tenant details...</p>
// //             </div>
// //           ) : !data ? (
// //             <div className="oc-modal-empty">
// //               <p>No tenant data found</p>
// //             </div>
// //           ) : (
// //             <div className="tenant-register-details">
// //               {/* Personal Information */}
// //               <div className="tenant-detail-section">
// //                 <h4 className="tenant-detail-section-title">👤 Personal Information</h4>
// //                 <div className="tenant-detail-grid">
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Tenant ID</span>
// //                     <span className="tenant-detail-value">{data.TenantId || '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Admission ID</span>
// //                     <span className="tenant-detail-value">{data.AdmissionId || '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Full Name</span>
// //                     <span className="tenant-detail-value">{data.FullName || '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Gender</span>
// //                     <span className="tenant-detail-value">{data.Gender || '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Date of Birth</span>
// //                     <span className="tenant-detail-value">{formatDateFull(data['Date Of Birth'])}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Mobile Number</span>
// //                     <span className="tenant-detail-value">{data.MobileNumber || '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Email Address</span>
// //                     <span className="tenant-detail-value">{data.EmailId || '—'}</span>
// //                   </div>
// //                 </div>
// //               </div>

// //               {/* Emergency Contact */}
// //               <div className="tenant-detail-section">
// //                 <h4 className="tenant-detail-section-title">🆘 Emergency Contact</h4>
// //                 <div className="tenant-detail-grid">
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Contact Name</span>
// //                     <span className="tenant-detail-value">{data.EmergenctContactName || '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Contact Number</span>
// //                     <span className="tenant-detail-value">{data.EmergencyContactNumber || '—'}</span>
// //                   </div>
// //                 </div>
// //               </div>

// //               {/* PG & Room Details */}
// //               <div className="tenant-detail-section">
// //                 <h4 className="tenant-detail-section-title">🏢 PG & Room Details</h4>
// //                 <div className="tenant-detail-grid">
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">PG Name</span>
// //                     <span className="tenant-detail-value">{data.PGName || '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Room Number</span>
// //                     <span className="tenant-detail-value">{data.RoomNumber || '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Bed Number</span>
// //                     <span className="tenant-detail-value">{data.BedNumber || '—'}</span>
// //                   </div>
// //                 </div>
// //               </div>

// //               {/* Tenancy Dates */}
// //               <div className="tenant-detail-section">
// //                 <h4 className="tenant-detail-section-title">📅 Tenancy Dates</h4>
// //                 <div className="tenant-detail-grid">
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Joining Date</span>
// //                     <span className="tenant-detail-value">{formatDateFull(data.JoinDate)}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Notice Date</span>
// //                     <span className="tenant-detail-value">{data.NoticeDate ? formatDateFull(data.NoticeDate) : '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Vacate Date</span>
// //                     <span className="tenant-detail-value">{data.VacateDate ? formatDateFull(data.VacateDate) : '—'}</span>
// //                   </div>
// //                   <div className="tenant-detail-item">
// //                     <span className="tenant-detail-label">Exit Date</span>
// //                     <span className="tenant-detail-value">
// //                       {data.ExitDate ? (
// //                         formatDateFull(data.ExitDate)
// //                       ) : (
// //                         <span className="tenant-status-active">-</span>
// //                       )}
// //                     </span>
// //                   </div>
// //                 </div>
// //               </div>

// //               {/* Status Summary */}
// //               {/* <div className="tenant-detail-section">
// //                 <h4 className="tenant-detail-section-title">📊 Status Summary</h4>
// //                 <div className="tenant-status-summary">
// //                   <div className="tenant-status-item">
// //                     <span className="tenant-status-label">Current Status</span>
// //                     <span className={`tenant-status-badge ${data.ExitDate ? 'status-exited' : 'status-active'}`}>
// //                       {data.ExitDate ? 'Exited' : 'Active'}
// //                     </span>
// //                   </div>
// //                   <div className="tenant-status-item">
// //                     <span className="tenant-status-label">Tenure</span>
// //                     <span className="tenant-status-value">
// //                       {data.JoinDate && !data.ExitDate ? 'Ongoing' : 
// //                        data.JoinDate && data.ExitDate ? 'Completed' : '—'}
// //                     </span>
// //                   </div>
// //                 </div>
// //               </div> */}
// //             </div>
// //           )}
// //         </div>
// //       </div>
// //     </div>
// //   )
// // }

// // ─── Tenant Register Modal ─────────────────────────────────────────
// const TenantRegisterModal = ({ tenantData, onClose, loading }) => {
//   if (!tenantData) return null

//   // Check if we have data (could be array or single object)
//   const data = Array.isArray(tenantData) ? tenantData[0] : tenantData

//   return (
//     <div className="oc-modal-overlay" onClick={onClose}>
//       <div className="oc-modal oc-modal-large" onClick={e => e.stopPropagation()}>
//         <div className="oc-modal-head">
//           <div>
//             <h3 className="oc-modal-title">📋 Tenant Register Details</h3>
//             <p className="oc-modal-sub">Complete tenant information</p>
//           </div>
//           <button className="oc-modal-close" onClick={onClose}>
//             <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
//               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
//             </svg>
//           </button>
//         </div>
//         <div className="oc-modal-body oc-modal-body-no-scroll">
//           {loading ? (
//             <div className="oc-loading">
//               <div className="oc-spinner"></div>
//               <p>Loading tenant details...</p>
//             </div>
//           ) : !data ? (
//             <div className="oc-modal-empty">
//               <p>No tenant data found</p>
//             </div>
//           ) : (
//             <div className="tenant-register-details">
//               {/* Row 1: Personal Information - 2 columns */}
//               <div className="tenant-detail-row">
//                 <div className="tenant-detail-col">
//                   <h4 className="tenant-detail-section-title">👤 Personal Information</h4>
//                   <div className="tenant-detail-grid">
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Tenant ID</span>
//                       <span className="tenant-detail-value">{data.TenantId || '—'}</span>
//                     </div>
//                     {/* <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Admission ID</span>
//                       <span className="tenant-detail-value">{data.AdmissionId || '—'}</span>
//                     </div> */}
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Full Name</span>
//                       <span className="tenant-detail-value">{data.FullName || '—'}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Gender</span>
//                       <span className="tenant-detail-value">{data.Gender || '—'}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Date of Birth</span>
//                       <span className="tenant-detail-value">{formatDateFull(data['Date Of Birth'])}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Mobile Number</span>
//                       <span className="tenant-detail-value">{data.MobileNumber || '—'}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Email Address</span>
//                       <span className="tenant-detail-value">{data.EmailId || '—'}</span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Row 1 Col 2: Emergency Contact */}
//                 <div className="tenant-detail-col">
//                   <h4 className="tenant-detail-section-title">🆘 Emergency Contact</h4>
//                   <div className="tenant-detail-grid">
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Contact Name</span>
//                       <span className="tenant-detail-value">{data.EmergenctContactName || '—'}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Contact Number</span>
//                       <span className="tenant-detail-value">{data.EmergencyContactNumber || '—'}</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Row 2: PG & Room Details + Tenancy Dates */}
//               <div className="tenant-detail-row">
//                 <div className="tenant-detail-col">
//                   <h4 className="tenant-detail-section-title">🏢 PG & Room Details</h4>
//                   <div className="tenant-detail-grid">
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">PG Name</span>
//                       <span className="tenant-detail-value">{data.PGName || '—'}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Room Number</span>
//                       <span className="tenant-detail-value">{data.RoomNumber || '—'}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Bed Number</span>
//                       <span className="tenant-detail-value">{data.BedNumber || '—'}</span>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="tenant-detail-col">
//                   <h4 className="tenant-detail-section-title">📅 Tenancy Dates</h4>
//                   <div className="tenant-detail-grid">
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Joining Date</span>
//                       <span className="tenant-detail-value">{formatDateFull(data.JoinDate)}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Notice Date</span>
//                       <span className="tenant-detail-value">{data.NoticeDate ? formatDateFull(data.NoticeDate) : '—'}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Vacate Date</span>
//                       <span className="tenant-detail-value">{data.VacateDate ? formatDateFull(data.VacateDate) : '—'}</span>
//                     </div>
//                     <div className="tenant-detail-item">
//                       <span className="tenant-detail-label">Exit Date</span>
//                       <span className="tenant-detail-value">
//                         {data.ExitDate ? (
//                           formatDateFull(data.ExitDate)
//                         ) : (
//                           <span className="tenant-status-active">—</span>
//                         )}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Row 3: Status Summary - Full width */}
//               {/* <div className="tenant-detail-row-full">
//                 <h4 className="tenant-detail-section-title">📊 Status Summary</h4>
//                 <div className="tenant-status-summary">
//                   <div className="tenant-status-item">
//                     <span className="tenant-status-label">Current Status</span>
//                     <span className={`tenant-status-badge ${data.ExitDate ? 'status-exited' : 'status-active'}`}>
//                       {data.ExitDate ? 'Exited' : 'Active'}
//                     </span>
//                   </div>
//                   <div className="tenant-status-item">
//                     <span className="tenant-status-label">Tenure</span>
//                     <span className="tenant-status-value">
//                       {data.JoinDate && !data.ExitDate ? 'Ongoing' : 
//                        data.JoinDate && data.ExitDate ? 'Completed' : '—'}
//                     </span>
//                   </div>
//                   <div className="tenant-status-item">
//                     <span className="tenant-status-label">Duration</span>
//                     <span className="tenant-status-value">
//                       {data.JoinDate && data.VacateDate ? (
//                         `${Math.ceil((new Date(data.VacateDate) - new Date(data.JoinDate)) / (1000 * 60 * 60 * 24 * 30))} months`
//                       ) : data.JoinDate ? (
//                         `${Math.ceil((new Date() - new Date(data.JoinDate)) / (1000 * 60 * 60 * 24 * 30))} months (ongoing)`
//                       ) : '—'}
//                     </span>
//                   </div>
//                 </div>
//               </div> */}
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   )
// }

// // ─── Record Modal ─────────────────────────────────────────────────
// const RecordModal = ({ title, sub, rows, columns, onClose }) => (
//   <div className="oc-modal-overlay" onClick={onClose}>
//     <div className="oc-modal" onClick={e => e.stopPropagation()}>
//       <div className="oc-modal-head">
//         <div>
//           <h3 className="oc-modal-title">{title}</h3>
//           <p className="oc-modal-sub">{sub}</p>
//         </div>
//         <button className="oc-modal-close" onClick={onClose}>
//           <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
//           </svg>
//         </button>
//       </div>
//       <div className="oc-modal-body">
//         {rows.length === 0 ? (
//           <p className="oc-modal-empty">No records found.</p>
//         ) : (
//           <table className="oc-modal-table">
//             <thead>
//               <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
//             </thead>
//             <tbody>
//               {rows.map((row, i) => (
//                 <tr key={i}>{columns.map(c => <td key={c.key}>{row[c.key]}</td>)}</tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </div>
//     </div>
//   </div>
// )

// // ─── Records Table Head ───────────────────────────────────────────
// const RecordsHead = () => (
//   <div className="oc-rec-head">
//     <div className="oc-rc oc-rc-room">Room</div>
//     <div className="oc-rc oc-rc-tenant">Tenant Name</div>
//     <div className="oc-rc oc-rc-reg">Tenant Register</div>
//     <div className="oc-rc oc-rc-police">Police Verification</div>
//     <div className="oc-rc oc-rc-checkin">Check-In/Out</div>
//     <div className="oc-rc oc-rc-visitors">Visitors</div>
//     <div className="oc-rc oc-rc-agreement">Agreement</div>
//     <div className="oc-rc oc-rc-download">Download All</div>
//   </div>
// )

// // ─── Pending Requests Tab ─────────────────────────────────────────
// const PendingRequests = ({ complianceData, onUpdateStatus, loading }) => {
//   const [filter, setFilter] = useState('all')
//   const [page, setPage] = useState(1)

//   const pendingRequests = complianceData.filter(item => 
//     item.ComplianceType !== 'Police Verification' && 
//     item.Status === 'PENDING'
//   )

//   const getType = (complianceType) => {
//     if (complianceType?.toLowerCase().includes('visitor')) return 'visitor'
//     if (complianceType?.toLowerCase().includes('check') || complianceType?.toLowerCase().includes('in/out')) return 'checkin'
//     return 'other'
//   }

//   const filtered = pendingRequests.filter(item => {
//     if (filter === 'all') return true
//     return getType(item.ComplianceType) === filter
//   })

//   const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
//   const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

//   const policeVerificationPending = complianceData.filter(item => 
//     item.ComplianceType === 'Police Verification' && 
//     item.Status === 'PENDING'
//   )

//   if (loading) {
//     return (
//       <div className="oc-root">
//         <div className="oc-loading">
//           <div className="oc-spinner"></div>
//           <p>Loading compliance data...</p>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="oc-root">
//       <div className="oc-section">
//         <div className="oc-section-title-row">
//           <h3 className="oc-section-title">Pending Requests</h3>
//           <span className="oc-count-badge">{pendingRequests.length}</span>
//         </div>

//         <div className="oc-filter-pills">
//           {[
//             ['all', 'All', pendingRequests.length],
//             ['visitor', 'Visitor', pendingRequests.filter(i => getType(i.ComplianceType) === 'visitor').length],
//             ['checkin', 'Check-In / Check-Out', pendingRequests.filter(i => getType(i.ComplianceType) === 'checkin').length]
//           ].map(([key, label, count]) => (
//             <button 
//               key={key} 
//               className={`oc-filter-pill ${filter === key ? 'active' : ''}`}
//               onClick={() => { setFilter(key); setPage(1) }}
//             >
//               {label}
//               {count > 0 && <span className="oc-pill-count">{count}</span>}
//             </button>
//           ))}
//         </div>

//         {filtered.length === 0 ? (
//           <div className="oc-table-empty">
//             <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
//               <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
//             </svg>
//             <p>No pending requests</p>
//           </div>
//         ) : (
//           <div className="oc-table">
//             <div className="oc-table-head">
//               <div className="oc-col oc-col-room">Room</div>
//               <div className="oc-col oc-col-tenant">Tenant</div>
//               <div className="oc-col oc-col-type">Type</div>
//               <div className="oc-col oc-col-details">Details</div>
//               <div className="oc-col oc-col-date">Date Range</div>
//               <div className="oc-col oc-col-actions">Actions</div>
//             </div>
//             {paged.map(item => (
//               <div key={item.ComplianceId} className="oc-table-row">
//                 <div className="oc-col oc-col-room">{item.RoomNumber || '—'}</div>
//                 <div className="oc-col oc-col-tenant">
//                   <strong>{item.tenantName}</strong>
//                 </div>
//                 <div className="oc-col oc-col-type">
//                   <span className={`oc-type-badge oc-type-${getType(item.ComplianceType)}`}>
//                     {getType(item.ComplianceType) === 'visitor'
//                       ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
//                       : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
//                     }
//                     {getType(item.ComplianceType) === 'visitor' ? 'Visitor' : 'Check-In/Out'}
//                   </span>
//                 </div>
//                 <div className="oc-col oc-col-details">
//                   <span className="oc-detail-main">{item.ComplianceDetails || '—'}</span>
//                 </div>
//                 <div className="oc-col oc-col-date">{item.DateRange || formatDate(item.CreatedDate)}</div>
//                 <div className="oc-col oc-col-actions">
//                   <button 
//                     className="oc-btn oc-btn-approve" 
//                     onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
//                   >
//                     Approve
//                   </button>
//                   <button 
//                     className="oc-btn-text-reject" 
//                     onClick={() => onUpdateStatus(item.ComplianceId, 'REJECTED')}
//                   >
//                     Reject
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         {totalPages > 1 && (
//           <div className="oc-pagination">
//             <span className="oc-page-info">Page {page} of {totalPages}</span>
//             <div className="oc-page-btns">
//               <button className="oc-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
//                 <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="2"/></svg>
//               </button>
//               <button className="oc-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
//                 <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2"/></svg>
//               </button>
//             </div>
//           </div>
//         )}
//       </div>

//       <div className="oc-section oc-police-verification">
//         <div className="oc-section-title-row">
//           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
//           <h3 className="oc-section-title">Police Verification Pending</h3>
//           <span className="oc-count-badge oc-count-amber">{policeVerificationPending.length}</span>
//         </div>
        
//         {policeVerificationPending.length === 0 ? (
//           <div className="oc-table-empty">
//             <p>No police verification pending</p>
//           </div>
//         ) : (
//           <div className="oc-table">
//             <div className="oc-table-head">
//               <div className="oc-col oc-col-room">Room</div>
//               <div className="oc-col oc-col-tenant">Tenant Name</div>
//               <div className="oc-col oc-col-pv-status">Status</div>
//               <div className="oc-col oc-col-actions">Actions</div>
//             </div>
//             {policeVerificationPending.map(item => (
//               <div key={item.ComplianceId} className="oc-table-row">
//                 <div className="oc-col oc-col-room">{item.RoomNo || '—'}</div>
//                 <div className="oc-col oc-col-tenant">
//                   <strong>{item.TenantName || `Tenant #${item.TenantID}`}</strong>
//                 </div>
//                 <div className="oc-col oc-col-pv-status">
//                   <span className={`oc-pv-badge oc-pv-${item.Status?.toLowerCase()}`}>
//                     {item.Status?.charAt(0).toUpperCase() + item.Status?.slice(1).toLowerCase()}
//                   </span>
//                 </div>
//                 <div className="oc-col oc-col-actions">
//                   <button className="oc-btn-reminder">
//                     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
//                     Send Reminder
//                   </button>
//                   <button 
//                     className="oc-btn oc-btn-approve" 
//                     onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
//                   >
//                     <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2"/></svg>
//                     Mark Verified
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// // ─── Tenant Record Row ────────────────────────────────────────────
// const TenantRecordRow = ({ item, onViewAgreement, onViewTenantRegister }) => {
//   const [modal, setModal] = useState(null)
//   const ps = POLICE_CFG[item.Status] || POLICE_CFG.PENDING
//   const agreementStatus = item.Status === 'COMPLETED' ? 'active' : 'expired'
//   const agreementConfig = AGREEMENT_CFG[agreementStatus]

//   return (
//     <>
//       <div className="oc-rec-row">
//         <div className="oc-rc oc-rc-room">{item.RoomNumber || '—'}</div>
//         <div className="oc-rc oc-rc-tenant"><strong>{item.tenantName || `Tenant #${item.tenantId}`}</strong></div>
//         <div className="oc-rc oc-rc-reg">
//           <button 
//             className="oc-link-btn"
//             onClick={() => onViewTenantRegister(item)}
//           >
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2">
//               <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
//               <circle cx="12" cy="12" r="3"/>
//             </svg>
//             View
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-police">
//           <span className="oc-police-label" style={{ color: ps.color }}>
//             <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
//               {item.Status === 'COMPLETED'
//                 ? <path d="M2 6L5 9L10 3" stroke={ps.dot} strokeWidth="2" strokeLinecap="round"/>
//                 : <circle cx="6" cy="6" r="5" stroke={ps.dot} strokeWidth="1.5"/>}
//             </svg>
//             {ps.label}
//           </span>
//           <button className="oc-icon-btn" title="Download">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-checkin">
//           <button className="oc-link-btn" onClick={() => setModal('checkin')}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             0 records
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-visitors">
//           <button className="oc-link-btn" onClick={() => setModal('visitors')}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             0 visitors
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-agreement">
//           <span className="oc-agreement-badge" style={{ background: agreementConfig.bg, color: agreementConfig.text, borderColor: agreementConfig.border }}>
//             {agreementConfig.label}
//           </span>
//           <button 
//             className="oc-icon-btn" 
//             title="View agreement"
//             onClick={() => onViewAgreement(item)}
//           >
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-download">
//           <button className="oc-download-btn">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
//             Download All
//           </button>
//         </div>
//       </div>

//       {modal === 'checkin' && (
//         <RecordModal
//           title="Check-In / Check-Out Records"
//           sub={`${item.tenantName} · Room ${item.RoomNumber}`}
//           rows={item.CheckIns || []}
//           columns={[{ key: 'date', label: 'Date' }, { key: 'reason', label: 'Reason' }]}
//           onClose={() => setModal(null)}
//         />
//       )}
//       {modal === 'visitors' && (
//         <RecordModal
//           title="Visitor Records"
//           sub={`${item.tenantName} · Room ${item.RoomNumber}`}
//           rows={item.Visitors || []}
//           columns={[{ key: 'name', label: 'Visitor Name' }, { key: 'relation', label: 'Relation' }, { key: 'date', label: 'Date' }]}
//           onClose={() => setModal(null)}
//         />
//       )}
//     </>
//   )
// }

// // ─── Tenant Records Tab ───────────────────────────────────────────
// const TenantRecords = ({ complianceData, loading, onViewAgreement, onViewTenantRegister }) => {
//   const [search, setSearch] = useState({ mobile: '', room: '', tenant: '' })
//   const [dateFrom, setDateFrom] = useState('')
//   const [dateTo, setDateTo] = useState('')
//   const [statusFilter, setStatusFilter] = useState('all')
//   const [openMonths, setOpenMonths] = useState({})

//   const activeRecords = complianceData.filter(item => item.Status === 'COMPLETED')
//   const previousRecords = complianceData.filter(item => item.Status === 'REJECTED')

//   const applySearch = (records) => {
//     return records.filter(item => {
//       const ms = !search.mobile || (item.TenantName || '').toLowerCase().includes(search.mobile.toLowerCase())
//       const rs = !search.room || (item.RoomNo || '').includes(search.room)
//       const ts = !search.tenant || (item.TenantName || '').toLowerCase().includes(search.tenant.toLowerCase())
      
//       if (dateFrom && item.CreatedDate) {
//         const itemDate = new Date(item.CreatedDate)
//         const fromDate = new Date(dateFrom)
//         if (itemDate < fromDate) return false
//       }
//       if (dateTo && item.CreatedDate) {
//         const itemDate = new Date(item.CreatedDate)
//         const toDate = new Date(dateTo)
//         if (itemDate > toDate) return false
//       }
      
//       return ms && rs && ts
//     })
//   }

//   const filteredActive = applySearch(activeRecords)
//   const filteredPrevious = applySearch(previousRecords)

//   const toggleMonth = (month) => setOpenMonths(p => ({ ...p, [month]: !p[month] }))

//   const byMonth = {}
//   filteredPrevious.forEach(item => {
//     const month = new Date(item.CreatedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
//     if (!byMonth[month]) byMonth[month] = []
//     byMonth[month].push(item)
//   })
//   const sortedMonths = Object.keys(byMonth).sort((a, b) => new Date(b) - new Date(a))

//   if (loading) {
//     return (
//       <div className="oc-root">
//         <div className="oc-loading">
//           <div className="oc-spinner"></div>
//           <p>Loading compliance data...</p>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="oc-root">
//       <div className="oc-filter-bar">
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Mobile</label>
//           <input className="oc-filter-input" placeholder="Search mobile..." value={search.mobile}
//             onChange={e => setSearch(p => ({ ...p, mobile: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Room</label>
//           <input className="oc-filter-input" placeholder="Room no." value={search.room}
//             onChange={e => setSearch(p => ({ ...p, room: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Tenant</label>
//           <input className="oc-filter-input" placeholder="Name..." value={search.tenant}
//             onChange={e => setSearch(p => ({ ...p, tenant: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">From</label>
//           <div className="oc-date-wrap">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg>
//             <input type="date" className="oc-filter-input oc-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">To</label>
//           <div className="oc-date-wrap">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg>
//             <input type="date" className="oc-filter-input oc-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Status</label>
//           <select className="oc-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
//             <option value="all">All</option>
//             <option value="active">Active</option>
//             <option value="previous">Previous</option>
//           </select>
//         </div>
//       </div>

//       {(statusFilter === 'all' || statusFilter === 'active') && (
//         <div className="oc-section">
//           <div className="oc-section-title-row">
//             <h3 className="oc-section-title">Active Tenants</h3>
//             <span className="oc-count-badge">{filteredActive.length}</span>
//           </div>
//           {filteredActive.length === 0 ? (
//             <div className="oc-table-empty">
//               <p>No active tenants found</p>
//             </div>
//           ) : (
//             <div className="oc-records-wrap">
//               <RecordsHead />
//               {filteredActive.map(item => (
//                 <TenantRecordRow 
//                   key={item.ComplianceId} 
//                   item={item} 
//                   onViewAgreement={onViewAgreement}
//                   onViewTenantRegister={onViewTenantRegister}
//                 />
//               ))}
//             </div>
//           )}
//         </div>
//       )}

//       {(statusFilter === 'all' || statusFilter === 'previous') && filteredPrevious.length > 0 && (
//         <div className="oc-section">
//           <div className="oc-section-title-row">
//             <h3 className="oc-section-title">Previous Tenants</h3>
//             <span className="oc-count-badge oc-count-muted">{filteredPrevious.length}</span>
//           </div>
//           <div className="oc-accordion">
//             {sortedMonths.map(month => {
//               const items = byMonth[month]
//               const isOpen = openMonths[month]
//               return (
//                 <div key={month} className="oc-accordion-item">
//                   <button className="oc-accordion-head" onClick={() => toggleMonth(month)}>
//                     <div className="oc-accordion-title">
//                       <span className="oc-accordion-month">{month}</span>
//                       <span className="oc-accordion-count">{items.length} tenant{items.length !== 1 ? 's' : ''}</span>
//                     </div>
//                     <svg className={`oc-chevron ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
//                       <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
//                     </svg>
//                   </button>
//                   {isOpen && (
//                     <div className="oc-accordion-body">
//                       <RecordsHead />
//                       {items.map(item => (
//                         <TenantRecordRow 
//                           key={item.ComplianceId} 
//                           item={item} 
//                           onViewAgreement={onViewAgreement}
//                           onViewTenantRegister={onViewTenantRegister}
//                         />
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )
//             })}
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }

// // ─── Main ─────────────────────────────────────────────────────────
// const OwnerCompliance = ({ selectedPG, selectedPGId }) => {
//   const [activeTab, setActiveTab] = useState('pending')
//   const [complianceData, setComplianceData] = useState([])
//   const [loading, setLoading] = useState(false)
//   const [error, setError] = useState(null)
//   const [showAgreement, setShowAgreement] = useState(false)
//   const [agreementFileName, setAgreementFileName] = useState('')
  
//   // Tenant Register Modal state
//   const [showTenantRegister, setShowTenantRegister] = useState(false)
//   const [tenantRegisterData, setTenantRegisterData] = useState(null)
//   const [tenantRegisterLoading, setTenantRegisterLoading] = useState(false)

//   useEffect(() => {
//     if (selectedPGId) {
//       fetchComplianceData()
//     }
//   }, [selectedPGId])

//   const fetchComplianceData = async () => {
//     setLoading(true)
//     setError(null)
//     try {
//       const response = await getAllComplianceByPGID(selectedPGId)
//       if (response && response.length > 0) {
//         setComplianceData(response)
//       } else {
//         setComplianceData([])
//       }
//     } catch (err) {
//       console.error('Error fetching compliance data:', err)
//       setError('Failed to load compliance data')
//       setComplianceData([])
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleUpdateStatus = async (complianceId, newStatus) => {
//     console.log("Updating compliance status:", { complianceId, newStatus });
    
//     try {
//       const response = await updateComplianceStatus({
//         ComplianceID: parseInt(complianceId),
//         Status: newStatus
//       })
//       console.log("response from updateComplianceStatus:", response);
      
//       if (response.Success === 1) {
//         await fetchComplianceData()
//         alert('Status updated successfully!')
//       } else {
//         alert(response.Message || 'Failed to update status')
//       }
//     } catch (err) {
//       console.error('Error updating status:', err)
//       alert('Failed to update status')
//     }
//   }

//   const handleViewAgreement = (item) => {
//     const fileName = `Rent_Agreement_Room-${item.RoomNumber}_${(item.tenantName || 'Tenant').replace(/\s+/g, '_')}.pdf`
//     setAgreementFileName(fileName)
//     setShowAgreement(true)
//   }

//   const handleViewTenantRegister = async (item) => {
//     // Get AdmissionID from the compliance item
//     const admissionId = item.AdmissionID || item.AdmissionId
    
//     if (!admissionId) {
//       alert('No Admission ID found for this tenant')
//       return
//     }

//     setShowTenantRegister(true)
//     setTenantRegisterLoading(true)
//     setTenantRegisterData(null)

//     try {
//       // Get current date for fromDate and toDate (or use a wider range)
//       const today = new Date()
//       const fromDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
//         .toISOString().split('T')[0] // 1 year ago
//       const toDate = today.toISOString().split('T')[0] // today
      
//       // PG ID - using selectedPGId
//       const pgId = selectedPGId

//       console.log('Fetching tenant register with:', { pgId, admissionId, fromDate, toDate })
      
//       const response = await getTenantRegisterByAdmission(pgId, admissionId, fromDate, toDate)
//       console.log('Tenant Register Response:', response)
      
//       if (response.success) {
//         // Response might be an array or object
//         setTenantRegisterData(response.tenants || response.data || response)
//       } else {
//         alert(response.message || 'Failed to fetch tenant details')
//         setShowTenantRegister(false)
//       }
//     } catch (err) {
//       console.error('Error fetching tenant register:', err)
//       alert('Failed to fetch tenant details')
//       setShowTenantRegister(false)
//     } finally {
//       setTenantRegisterLoading(false)
//     }
//   }

//   if (error) {
//     return (
//       <div className="ocomp-root">
//         <div className="oc-error">
//           <p>{error}</p>
//           <button onClick={fetchComplianceData} className="oc-retry-btn">Retry</button>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="ocomp-root">
//       <div className="ocomp-tabs">
//         <button className={`ocomp-tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
//           Pending Requests
//         </button>
//         <button className={`ocomp-tab ${activeTab === 'records' ? 'active' : ''}`} onClick={() => setActiveTab('records')}>
//           Tenant Compliance Records
//         </button>
//       </div>
      
//       {activeTab === 'pending' && (
//         <PendingRequests 
//           complianceData={complianceData}
//           onUpdateStatus={handleUpdateStatus}
//           loading={loading}
//         />
//       )}
      
//       {activeTab === 'records' && (
//         <TenantRecords 
//           complianceData={complianceData}
//           loading={loading}
//           onViewAgreement={handleViewAgreement}
//           onViewTenantRegister={handleViewTenantRegister}
//         />
//       )}

//       {showAgreement && (
//         <ViewAgreementModal
//           fileName={agreementFileName}
//           onClose={() => setShowAgreement(false)}
//         />
//       )}

//       {/* Tenant Register Modal */}
//       {showTenantRegister && (
//         <TenantRegisterModal
//           tenantData={tenantRegisterData}
//           loading={tenantRegisterLoading}
//           onClose={() => {
//             setShowTenantRegister(false)
//             setTenantRegisterData(null)
//           }}
//         />
//       )}

//       {/* Add styles for tenant register modal */}
//       {/* <style>{`
//         .oc-modal-large {
//           max-width: 900px !important;
//           width: 90% !important;
//         }

//         .tenant-register-details {
//           padding: 4px 0;
//         }

//         .tenant-detail-section {
//           margin-bottom: 24px;
//           padding-bottom: 20px;
//           border-bottom: 1px solid #E5E7EB;
//         }

//         .tenant-detail-section:last-child {
//           border-bottom: none;
//           margin-bottom: 0;
//         }

//         .tenant-detail-section-title {
//           font-size: 14px;
//           font-weight: 600;
//           color: #374151;
//           margin: 0 0 16px 0;
//           padding-bottom: 8px;
//           border-bottom: 2px solid #6366F1;
//           display: inline-block;
//         }

//         .tenant-detail-grid {
//           display: grid;
//           grid-template-columns: repeat(2, 1fr);
//           gap: 12px 24px;
//         }

//         .tenant-detail-item {
//           display: flex;
//           flex-direction: column;
//           padding: 8px 0;
//         }

//         .tenant-detail-label {
//           font-size: 11px;
//           font-weight: 500;
//           color: #6B7280;
//           text-transform: uppercase;
//           letter-spacing: 0.5px;
//           margin-bottom: 4px;
//         }

//         .tenant-detail-value {
//           font-size: 14px;
//           color: #111827;
//           font-weight: 500;
//         }

//         .tenant-status-active {
//           color: #16A34A;
//           font-weight: 600;
//         }

//         .tenant-status-summary {
//           display: flex;
//           gap: 32px;
//           padding: 12px 0;
//         }

//         .tenant-status-item {
//           display: flex;
//           flex-direction: column;
//         }

//         .tenant-status-label {
//           font-size: 11px;
//           font-weight: 500;
//           color: #6B7280;
//           text-transform: uppercase;
//           letter-spacing: 0.5px;
//           margin-bottom: 4px;
//         }

//         .tenant-status-badge {
//           display: inline-block;
//           padding: 4px 12px;
//           border-radius: 12px;
//           font-size: 12px;
//           font-weight: 600;
//         }

//         .tenant-status-badge.status-active {
//           background: #F0FDF4;
//           color: #166534;
//         }

//         .tenant-status-badge.status-exited {
//           background: #FEF2F2;
//           color: #991B1B;
//         }

//         .tenant-status-value {
//           font-size: 14px;
//           color: #111827;
//           font-weight: 500;
//         }

//         .oc-modal-empty {
//           text-align: center;
//           padding: 40px 20px;
//           color: #6B7280;
//         }

//         @media (max-width: 640px) {
//           .tenant-detail-grid {
//             grid-template-columns: 1fr;
//           }
          
//           .tenant-status-summary {
//             flex-direction: column;
//             gap: 12px;
//           }
//         }
//       `}</style> */}

//       <style>{`
//   .oc-modal-large {
//     max-width: 900px !important;
//     width: 90% !important;
//     max-height: 90vh !important;
//   }

//   .oc-modal-body-no-scroll {
//     overflow: visible !important;
//     padding: 20px 24px !important;
//     max-height: none !important;
//   }

//   .tenant-register-details {
//     display: flex;
//     flex-direction: column;
//     gap: 16px;
//   }

//   .tenant-detail-row {
//     display: grid;
//     grid-template-columns: 1fr 1fr;
//     gap: 24px;
//     padding: 12px 0;
//     border-bottom: 1px solid #F3F4F6;
//   }

//   .tenant-detail-row:last-child {
//     border-bottom: none;
//   }

//   .tenant-detail-row-full {
//     padding: 12px 0 0 0;
//   }

//   .tenant-detail-col {
//     display: flex;
//     flex-direction: column;
//   }

//   .tenant-detail-section-title {
//     font-size: 13px;
//     font-weight: 600;
//     color: #374151;
//     margin: 0 0 12px 0;
//     padding-bottom: 6px;
//     border-bottom: 2px solid #6366F1;
//     display: inline-block;
//     width: auto;
//   }

//   .tenant-detail-grid {
//     display: grid;
//     grid-template-columns: 1fr;
//     gap: 6px 0;
//   }

//   .tenant-detail-item {
//     display: flex;
//     justify-content: space-between;
//     align-items: center;
//     padding: 4px 0;
//     border-bottom: 1px solid #F9FAFB;
//   }

//   .tenant-detail-item:last-child {
//     border-bottom: none;
//   }

//   .tenant-detail-label {
//     font-size: 12px;
//     font-weight: 500;
//     color: #6B7280;
//     text-transform: uppercase;
//     letter-spacing: 0.3px;
//   }

//   .tenant-detail-value {
//     font-size: 13px;
//     color: #111827;
//     font-weight: 500;
//     text-align: right;
//     max-width: 60%;
//     word-break: break-word;
//   }

//   .tenant-status-active {
//     color: #16A34A;
//     font-weight: 600;
//   }

//   .tenant-status-summary {
//     display: flex;
//     gap: 40px;
//     padding: 8px 0;
//     flex-wrap: wrap;
//   }

//   .tenant-status-item {
//     display: flex;
//     flex-direction: column;
//     gap: 4px;
//   }

//   .tenant-status-label {
//     font-size: 11px;
//     font-weight: 500;
//     color: #6B7280;
//     text-transform: uppercase;
//     letter-spacing: 0.3px;
//   }

//   .tenant-status-badge {
//     display: inline-block;
//     padding: 3px 12px;
//     border-radius: 12px;
//     font-size: 12px;
//     font-weight: 600;
//     width: fit-content;
//   }

//   .tenant-status-badge.status-active {
//     background: #F0FDF4;
//     color: #166534;
//   }

//   .tenant-status-badge.status-exited {
//     background: #FEF2F2;
//     color: #991B1B;
//   }

//   .tenant-status-value {
//     font-size: 13px;
//     color: #111827;
//     font-weight: 500;
//   }

//   .oc-modal-empty {
//     text-align: center;
//     padding: 40px 20px;
//     color: #6B7280;
//   }

//   /* Responsive */
//   @media (max-width: 768px) {
//     .tenant-detail-row {
//       grid-template-columns: 1fr;
//       gap: 16px;
//     }

//     .tenant-detail-item {
//       flex-direction: column;
//       align-items: flex-start;
//       gap: 2px;
//     }

//     .tenant-detail-value {
//       text-align: left;
//       max-width: 100%;
//     }

//     .tenant-status-summary {
//       flex-direction: column;
//       gap: 12px;
//     }

//     .oc-modal-large {
//       width: 95% !important;
//       max-height: 95vh !important;
//     }

//     .oc-modal-body-no-scroll {
//       padding: 16px !important;
//     }
//   }

//   @media (max-width: 480px) {
//     .tenant-detail-section-title {
//       font-size: 12px;
//     }

//     .tenant-detail-label {
//       font-size: 10px;
//     }

//     .tenant-detail-value {
//       font-size: 12px;
//     }
//   }
// `}</style>
//     </div>
//   )
// }

// export default OwnerCompliance



// import '../scss/components/_ownercompliance.scss'
// import React, { useState, useEffect } from 'react'
// import { getAllComplianceByPGID, updateComplianceStatus } from '../apis/services/pgApi'
// import ViewAgreementModal from './RentAgreement'
// import agreementData from '../json/rentagreementdata.json'

// // ─── Constants ────────────────────────────────────────────────────
// const POLICE_CFG = {
//   COMPLETED: { label: 'Completed', color: '#16A34A', dot: '#22C55E' },
//   PENDING: { label: 'Pending', color: '#D97706', dot: '#F59E0B' },
//   REJECTED: { label: 'Rejected', color: '#DC2626', dot: '#EF4444' },
//   IN_PROGRESS: { label: 'In Progress', color: '#3B82F6', dot: '#60A5FA' },
// }

// const AGREEMENT_CFG = {
//   active: { label: 'Active', bg: '#F0FDF4', text: '#166534', border: '#86EFAC' },
//   expired: { label: 'Expired', bg: '#FFF1F2', text: '#991B1B', border: '#FECACA' },
//   expiring_soon: { label: 'Expiring Soon', bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
// }

// const PAGE_SIZE = 5

// // ─── Helper Functions ─────────────────────────────────────────────────
// const formatDate = (dateString) => {
//   if (!dateString) return '—'
//   const date = new Date(dateString)
//   return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
// }

// // ─── Record Modal ─────────────────────────────────────────────────
// const RecordModal = ({ title, sub, rows, columns, onClose }) => (
//   <div className="oc-modal-overlay" >
//     <div className="oc-modal" onClick={e => e.stopPropagation()}>
//       <div className="oc-modal-head">
//         <div>
//           <h3 className="oc-modal-title">{title}</h3>
//           <p className="oc-modal-sub">{sub}</p>
//         </div>
//         <button className="oc-modal-close" onClick={onClose}>
//           <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
//           </svg>
//         </button>
//       </div>
//       <div className="oc-modal-body">
//         {rows.length === 0 ? (
//           <p className="oc-modal-empty">No records found.</p>
//         ) : (
//           <table className="oc-modal-table">
//             <thead>
//               <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
//             </thead>
//             <tbody>
//               {rows.map((row, i) => (
//                 <tr key={i}>{columns.map(c => <td key={c.key}>{row[c.key]}</td>)}</tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </div>
//     </div>
//   </div>
// )

// // ─── Records Table Head ───────────────────────────────────────────
// const RecordsHead = () => (
//   <div className="oc-rec-head">
//     <div className="oc-rc oc-rc-room">Room</div>
//     <div className="oc-rc oc-rc-tenant">Tenant Name</div>
//     <div className="oc-rc oc-rc-reg">Tenant Register</div>
//     <div className="oc-rc oc-rc-police">Police Verification</div>
//     <div className="oc-rc oc-rc-checkin">Check-In/Out</div>
//     <div className="oc-rc oc-rc-visitors">Visitors</div>
//     <div className="oc-rc oc-rc-agreement">Agreement</div>
//     <div className="oc-rc oc-rc-download">Download All</div>
//   </div>
// )

// // ─── Pending Requests Tab ─────────────────────────────────────────
// const PendingRequests = ({ complianceData, onUpdateStatus, loading }) => {
//   const [filter, setFilter] = useState('all')
//   const [page, setPage] = useState(1)

//   // Filter: Exclude Police Verification, only PENDING status
//   const pendingRequests = complianceData.filter(item => 
//     item.ComplianceType !== 'Police Verification' && 
//     item.Status === 'PENDING'
//   )

//   // Filter by type (Visitor or Check-In/Out)
//   const getType = (complianceType) => {
//     if (complianceType?.toLowerCase().includes('visitor')) return 'visitor'
//     if (complianceType?.toLowerCase().includes('check') || complianceType?.toLowerCase().includes('in/out')) return 'checkin'
//     return 'other'
//   }

//   const filtered = pendingRequests.filter(item => {
//     if (filter === 'all') return true
//     return getType(item.ComplianceType) === filter
//   })

//   const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
//   const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

//   // Police Verification pending items
//   const policeVerificationPending = complianceData.filter(item => 
//     item.ComplianceType === 'Police Verification' && 
//     item.Status === 'PENDING'
//   )

//   if (loading) {
//     return (
//       <div className="oc-root">
//         <div className="oc-loading">
//           <div className="oc-spinner"></div>
//           <p>Loading compliance data...</p>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="oc-root">
//       {/* Pending Requests Section */}
//       <div className="oc-section">
//         <div className="oc-section-title-row">
//           <h3 className="oc-section-title">Pending Requests</h3>
//           <span className="oc-count-badge">{pendingRequests.length}</span>
//         </div>

//         <div className="oc-filter-pills">
//           {[
//             ['all', 'All', pendingRequests.length],
//             ['visitor', 'Visitor', pendingRequests.filter(i => getType(i.ComplianceType) === 'visitor').length],
//             ['checkin', 'Check-In / Check-Out', pendingRequests.filter(i => getType(i.ComplianceType) === 'checkin').length]
//           ].map(([key, label, count]) => (
//             <button 
//               key={key} 
//               className={`oc-filter-pill ${filter === key ? 'active' : ''}`}
//               onClick={() => { setFilter(key); setPage(1) }}
//             >
//               {label}
//               {count > 0 && <span className="oc-pill-count">{count}</span>}
//             </button>
//           ))}
//         </div>

//         {filtered.length === 0 ? (
//           <div className="oc-table-empty">
//             <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
//               <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
//             </svg>
//             <p>No pending requests</p>
//           </div>
//         ) : (
//           <div className="oc-table">
//             <div className="oc-table-head">
//               <div className="oc-col oc-col-room">Room</div>
//               <div className="oc-col oc-col-tenant">Tenant</div>
//               <div className="oc-col oc-col-type">Type</div>
//               <div className="oc-col oc-col-details">Details</div>
//               <div className="oc-col oc-col-date">Date Range</div>
//               <div className="oc-col oc-col-actions">Actions</div>
//             </div>
//             {paged.map(item => (
//               <div key={item.ComplianceId} className="oc-table-row">
//                 <div className="oc-col oc-col-room">{item.RoomNumber || '—'}</div>
//                 <div className="oc-col oc-col-tenant">
//                   <strong>{item.tenantName}</strong>
//                 </div>
//                 <div className="oc-col oc-col-type">
//                   <span className={`oc-type-badge oc-type-${getType(item.ComplianceType)}`}>
//                     {getType(item.ComplianceType) === 'visitor'
//                       ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
//                       : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
//                     }
//                     {getType(item.ComplianceType) === 'visitor' ? 'Visitor' : 'Check-In/Out'}
//                   </span>
//                 </div>
//                 <div className="oc-col oc-col-details">
//                   <span className="oc-detail-main">{item.ComplianceDetails || '—'}</span>
//                 </div>
//                 <div className="oc-col oc-col-date">{item.DateRange || formatDate(item.CreatedDate)}</div>
//                 <div className="oc-col oc-col-actions">
//                   <button 
//                     className="oc-btn oc-btn-approve" 
//                     onClick={() => {
                      
//                       onUpdateStatus(item.ComplianceId, 'COMPLETED')}}
//                   >
//                     Approve
//                   </button>
//                   <button 
//                     className="oc-btn-text-reject" 
//                     onClick={() => onUpdateStatus(item.ComplianceId, 'REJECTED')}
//                   >
//                     Reject
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}

//         {totalPages > 1 && (
//           <div className="oc-pagination">
//             <span className="oc-page-info">Page {page} of {totalPages}</span>
//             <div className="oc-page-btns">
//               <button className="oc-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
//                 <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="2"/></svg>
//               </button>
//               <button className="oc-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
//                 <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2"/></svg>
//               </button>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Police Verification Section */}
//       <div className="oc-section oc-police-verification">
//         <div className="oc-section-title-row">
//           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
//           <h3 className="oc-section-title">Police Verification Pending</h3>
//           <span className="oc-count-badge oc-count-amber">{policeVerificationPending.length}</span>
//         </div>
        
//         {policeVerificationPending.length === 0 ? (
//           <div className="oc-table-empty">
//             <p>No police verification pending</p>
//           </div>
//         ) : (
//           <div className="oc-table">
//             <div className="oc-table-head">
//               <div className="oc-col oc-col-room">Room</div>
//               <div className="oc-col oc-col-tenant">Tenant Name</div>
//               <div className="oc-col oc-col-pv-status">Status</div>
//               <div className="oc-col oc-col-actions">Actions</div>
//             </div>
//             {policeVerificationPending.map(item => (
//               <div key={item.ComplianceId} className="oc-table-row">
//                 <div className="oc-col oc-col-room">{item.RoomNo || '—'}</div>
//                 <div className="oc-col oc-col-tenant">
//                   <strong>{item.TenantName || `Tenant #${item.TenantID}`}</strong>
//                 </div>
//                 <div className="oc-col oc-col-pv-status">
//                   <span className={`oc-pv-badge oc-pv-${item.Status?.toLowerCase()}`}>
//                     {item.Status?.charAt(0).toUpperCase() + item.Status?.slice(1).toLowerCase()}
//                   </span>
//                 </div>
//                 <div className="oc-col oc-col-actions">
//                   <button className="oc-btn-reminder">
//                     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
//                     Send Reminder
//                   </button>
//                   <button 
//                     className="oc-btn oc-btn-approve" 
//                     onClick={() => onUpdateStatus(item.ComplianceId, 'COMPLETED')}
//                   >
//                     <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2"/></svg>
//                     Mark Verified
//                   </button>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}
//       </div>

//       <style>{`
//         .oc-loading {
//           display: flex;
//           flex-direction: column;
//           align-items: center;
//           justify-content: center;
//           padding: 60px 20px;
//           text-align: center;
//         }
//         .oc-spinner {
//           width: 40px;
//           height: 40px;
//           border: 3px solid #E5E7EB;
//           border-top-color: #6366F1;
//           border-radius: 50%;
//           animation: oc-spin 0.8s linear infinite;
//           margin-bottom: 16px;
//         }
//         @keyframes oc-spin {
//           to { transform: rotate(360deg); }
//         }
//         .oc-table-empty {
//           display: flex;
//           flex-direction: column;
//           align-items: center;
//           justify-content: center;
//           padding: 48px 20px;
//           text-align: center;
//           background: #F9FAFB;
//           border-radius: 12px;
//           color: #6B7280;
//         }
//         .oc-table-empty svg {
//           margin-bottom: 12px;
//         }
//       `}</style>
//     </div>
//   )
// }

// // ─── Tenant Record Row ────────────────────────────────────────────
// const TenantRecordRow = ({ item, onViewAgreement }) => {
//   const [modal, setModal] = useState(null)
//   const ps = POLICE_CFG[item.Status] || POLICE_CFG.PENDING
//   const agreementStatus = item.Status === 'COMPLETED' ? 'active' : 'expired'
//   const agreementConfig = AGREEMENT_CFG[agreementStatus]

//   return (
//     <>
//       <div className="oc-rec-row">
//         <div className="oc-rc oc-rc-room">{item.RoomNumber || '—'}</div>
//         <div className="oc-rc oc-rc-tenant"><strong>{item.tenantName || `Tenant #${item.tenantId}`}</strong></div>
//         <div className="oc-rc oc-rc-reg">
//           <button className="oc-link-btn">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             View
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-police">
//           <span className="oc-police-label" style={{ color: ps.color }}>
//             <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
//               {item.Status === 'COMPLETED'
//                 ? <path d="M2 6L5 9L10 3" stroke={ps.dot} strokeWidth="2" strokeLinecap="round"/>
//                 : <circle cx="6" cy="6" r="5" stroke={ps.dot} strokeWidth="1.5"/>}
//             </svg>
//             {ps.label}
//           </span>
//           <button className="oc-icon-btn" title="Download">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-checkin">
//           <button className="oc-link-btn" onClick={() => setModal('checkin')}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             0 records
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-visitors">
//           <button className="oc-link-btn" onClick={() => setModal('visitors')}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             0 visitors
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-agreement">
//           <span className="oc-agreement-badge" style={{ background: agreementConfig.bg, color: agreementConfig.text, borderColor: agreementConfig.border }}>
//             {agreementConfig.label}
//           </span>
//           <button 
//             className="oc-icon-btn" 
//             title="View agreement"
//             onClick={() => onViewAgreement(item)}
//           >
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-download">
//           <button className="oc-download-btn">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
//             Download All
//           </button>
//         </div>
//       </div>

//       {modal === 'checkin' && (
//         <RecordModal
//           title="Check-In / Check-Out Records"
//           sub={`${item.tenantName} · Room ${item.RoomNumber}`}
//           rows={item.CheckIns || []}
//           columns={[{ key: 'date', label: 'Date' }, { key: 'reason', label: 'Reason' }]}
//           onClose={() => setModal(null)}
//         />
//       )}
//       {modal === 'visitors' && (
//         <RecordModal
//           title="Visitor Records"
//           sub={`${item.tenantName} · Room ${item.RoomNumber}`}
//           rows={item.Visitors || []}
//           columns={[{ key: 'name', label: 'Visitor Name' }, { key: 'relation', label: 'Relation' }, { key: 'date', label: 'Date' }]}
//           onClose={() => setModal(null)}
//         />
//       )}
//     </>
//   )
// }

// // ─── Tenant Records Tab ───────────────────────────────────────────
// const TenantRecords = ({ complianceData, loading, onViewAgreement }) => {
//   const [search, setSearch] = useState({ mobile: '', room: '', tenant: '' })
//   const [dateFrom, setDateFrom] = useState('')
//   const [dateTo, setDateTo] = useState('')
//   const [statusFilter, setStatusFilter] = useState('all')
//   const [openMonths, setOpenMonths] = useState({})

//   // Active tenants: Status = COMPLETED (Police Verification and others)
//   // For now, showing all completed items as active tenants
//   const activeRecords = complianceData.filter(item => item.Status === 'COMPLETED')
  
//   // Previous/Expired tenants: Status = REJECTED or EXPIRED
//   const previousRecords = complianceData.filter(item => item.Status === 'REJECTED')

//   const applySearch = (records) => {
//     return records.filter(item => {
//       const ms = !search.mobile || (item.TenantName || '').toLowerCase().includes(search.mobile.toLowerCase())
//       const rs = !search.room || (item.RoomNo || '').includes(search.room)
//       const ts = !search.tenant || (item.TenantName || '').toLowerCase().includes(search.tenant.toLowerCase())
      
//       if (dateFrom && item.CreatedDate) {
//         const itemDate = new Date(item.CreatedDate)
//         const fromDate = new Date(dateFrom)
//         if (itemDate < fromDate) return false
//       }
//       if (dateTo && item.CreatedDate) {
//         const itemDate = new Date(item.CreatedDate)
//         const toDate = new Date(dateTo)
//         if (itemDate > toDate) return false
//       }
      
//       return ms && rs && ts
//     })
//   }

//   const filteredActive = applySearch(activeRecords)
//   const filteredPrevious = applySearch(previousRecords)

//   const toggleMonth = (month) => setOpenMonths(p => ({ ...p, [month]: !p[month] }))

//   // Group previous records by month
//   const byMonth = {}
//   filteredPrevious.forEach(item => {
//     const month = new Date(item.CreatedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
//     if (!byMonth[month]) byMonth[month] = []
//     byMonth[month].push(item)
//   })
//   const sortedMonths = Object.keys(byMonth).sort((a, b) => new Date(b) - new Date(a))

//   if (loading) {
//     return (
//       <div className="oc-root">
//         <div className="oc-loading">
//           <div className="oc-spinner"></div>
//           <p>Loading compliance data...</p>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="oc-root">
//       {/* Filter bar */}
//       <div className="oc-filter-bar">
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Mobile</label>
//           <input className="oc-filter-input" placeholder="Search mobile..." value={search.mobile}
//             onChange={e => setSearch(p => ({ ...p, mobile: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Room</label>
//           <input className="oc-filter-input" placeholder="Room no." value={search.room}
//             onChange={e => setSearch(p => ({ ...p, room: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Tenant</label>
//           <input className="oc-filter-input" placeholder="Name..." value={search.tenant}
//             onChange={e => setSearch(p => ({ ...p, tenant: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">From</label>
//           <div className="oc-date-wrap">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg>
//             <input type="date" className="oc-filter-input oc-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">To</label>
//           <div className="oc-date-wrap">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg>
//             <input type="date" className="oc-filter-input oc-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Status</label>
//           <select className="oc-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
//             <option value="all">All</option>
//             <option value="active">Active</option>
//             <option value="previous">Previous</option>
//           </select>
//         </div>
//       </div>

//       {/* Active Tenants Section */}
//       {(statusFilter === 'all' || statusFilter === 'active') && (
//         <div className="oc-section">
//           <div className="oc-section-title-row">
//             <h3 className="oc-section-title">Active Tenants</h3>
//             <span className="oc-count-badge">{filteredActive.length}</span>
//           </div>
//           {filteredActive.length === 0 ? (
//             <div className="oc-table-empty">
//               <p>No active tenants found</p>
//             </div>
//           ) : (
//             <div className="oc-records-wrap">
//               <RecordsHead />
//               {filteredActive.map(item => (
//                 <TenantRecordRow 
//                   key={item.ComplianceId} 
//                   item={item} 
//                   onViewAgreement={onViewAgreement}
//                 />
//               ))}
//             </div>
//           )}
//         </div>
//       )}

//       {/* Previous Tenants Section */}
//       {(statusFilter === 'all' || statusFilter === 'previous') && filteredPrevious.length > 0 && (
//         <div className="oc-section">
//           <div className="oc-section-title-row">
//             <h3 className="oc-section-title">Previous Tenants</h3>
//             <span className="oc-count-badge oc-count-muted">{filteredPrevious.length}</span>
//           </div>
//           <div className="oc-accordion">
//             {sortedMonths.map(month => {
//               const items = byMonth[month]
//               const isOpen = openMonths[month]
//               return (
//                 <div key={month} className="oc-accordion-item">
//                   <button className="oc-accordion-head" onClick={() => toggleMonth(month)}>
//                     <div className="oc-accordion-title">
//                       <span className="oc-accordion-month">{month}</span>
//                       <span className="oc-accordion-count">{items.length} tenant{items.length !== 1 ? 's' : ''}</span>
//                     </div>
//                     <svg className={`oc-chevron ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
//                       <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
//                     </svg>
//                   </button>
//                   {isOpen && (
//                     <div className="oc-accordion-body">
//                       <RecordsHead />
//                       {items.map(item => (
//                         <TenantRecordRow 
//                           key={item.ComplianceId} 
//                           item={item} 
//                           onViewAgreement={onViewAgreement}
//                         />
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )
//             })}
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }

// // ─── Main ─────────────────────────────────────────────────────────
// const OwnerCompliance = ({ selectedPG, selectedPGId }) => {
//   const [activeTab, setActiveTab] = useState('pending')
//   const [complianceData, setComplianceData] = useState([])
//   const [loading, setLoading] = useState(false)
//   const [error, setError] = useState(null)
//   const [showAgreement, setShowAgreement] = useState(false)
//   const [agreementFileName, setAgreementFileName] = useState('')

//   useEffect(() => {
//     if (selectedPGId) {
//       fetchComplianceData()
//     }
//   }, [selectedPGId])

//   const fetchComplianceData = async () => {
//     setLoading(true)
//     setError(null)
//     try {
//       const response = await getAllComplianceByPGID(selectedPGId)
//       if (response && response.length > 0) {
//         setComplianceData(response)
//       } else {
//         setComplianceData([])
//       }
//     } catch (err) {
//       console.error('Error fetching compliance data:', err)
//       setError('Failed to load compliance data')
//       setComplianceData([])
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleUpdateStatus = async (complianceId, newStatus) => {
//     console.log("Updating compliance status:", { complianceId, newStatus });
    
//     try {
//       const response = await updateComplianceStatus({
//         ComplianceID: parseInt(complianceId),
//         Status: newStatus
//       })
//       console.log("response from updateComplianceStatus:", response);
      
//       if (response.Success === 1) {
//         await fetchComplianceData()
//         alert('Status updated successfully!')
//       } else {
//         alert(response.Message || 'Failed to update status')
//       }
//     } catch (err) {
//       console.error('Error updating status:', err)
//       alert('Failed to update status')
//     }
//   }

//   const handleViewAgreement = (item) => {
//     const fileName = `Rent_Agreement_Room-${item.RoomNumber}_${(item.tenantName || 'Tenant').replace(/\s+/g, '_')}.pdf`
//     setAgreementFileName(fileName)
//     setShowAgreement(true)
//   }

//   if (error) {
//     return (
//       <div className="ocomp-root">
//         <div className="oc-error">
//           <p>{error}</p>
//           <button onClick={fetchComplianceData} className="oc-retry-btn">Retry</button>
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="ocomp-root">
//       <div className="ocomp-tabs">
//         <button className={`ocomp-tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
//           Pending Requests
//         </button>
//         <button className={`ocomp-tab ${activeTab === 'records' ? 'active' : ''}`} onClick={() => setActiveTab('records')}>
//           Tenant Compliance Records
//         </button>
//       </div>
      
//       {activeTab === 'pending' && (
//         <PendingRequests 
//           complianceData={complianceData}
//           onUpdateStatus={handleUpdateStatus}
//           loading={loading}
//         />
//       )}
      
//       {activeTab === 'records' && (
//         <TenantRecords 
//           complianceData={complianceData}
//           loading={loading}
//           onViewAgreement={handleViewAgreement}
//         />
//       )}

//       {showAgreement && (
//         <ViewAgreementModal
//           fileName={agreementFileName}
//           onClose={() => setShowAgreement(false)}
//         />
//       )}
//     </div>
//   )
// }

// export default OwnerCompliance
























// import '../scss/components/_ownercompliance.scss'
// import React, { useState } from 'react'
// import ViewAgreementModal from './RentAgreement'
// import agreementData from '../json/rentagreementdata.json'

// // ─── Mock Data ────────────────────────────────────────────────────
// const PENDING_REQUESTS = [
//   { id: 1, room: '302', tenant: 'Tushar S.',  type: 'visitor', details: 'Mother visiting for 2 days',   person: 'Sunita Sharma (Mother)',  dateRange: 'Oct 12–14' },
//   { id: 2, room: '105', tenant: 'Jatin B.',   type: 'checkin', details: 'Personal leave – going home',  person: '',                        dateRange: 'Oct 15–20' },
//   { id: 3, room: '201', tenant: 'Divya J.',   type: 'visitor', details: 'Friend staying overnight',     person: 'Neha Singh (Friend)',     dateRange: 'Oct 18–19' },
//   { id: 4, room: '304', tenant: 'Rahul M.',   type: 'checkin', details: 'Room transfer from 301',       person: '',                        dateRange: 'Oct 25'    },
//   { id: 5, room: '203', tenant: 'Priya M.',   type: 'visitor', details: 'Brother visiting for weekend', person: 'Arjun Malhotra (Brother)', dateRange: 'Oct 20–22' },
//   { id: 6, room: '101', tenant: 'Anil K.',    type: 'checkin', details: 'Going home for festival',      person: '',                        dateRange: 'Oct 23–28' },
//   { id: 7, room: '205', tenant: 'Sonal T.',   type: 'visitor', details: 'Cousin visiting',              person: 'Rakesh Tiwari (Cousin)',  dateRange: 'Oct 26–27' },
// ]

// const POLICE_VERIFICATION = [
//   { id: 1, room: '105', tenant: 'Jatin B.',  status: 'pending'  },
//   { id: 2, room: '304', tenant: 'Rahul M.',  status: 'rejected' },
//   { id: 3, room: '203', tenant: 'Priya M.',  status: 'pending'  },
// ]

// // leftMonth: which month they left (for accordion grouping), null = active
// const COMPLIANCE_RECORDS = [
//   { id: 1, room: '302', tenant: 'Tushar S.', leftMonth: null,           policeVerif: 'verified', checkIns: [{ date: 'Sep 15', reason: 'Went home' }, { date: 'Oct 2', reason: 'Festival leave' }, { date: 'Oct 18', reason: 'Family visit' }], visitors: [{ name: 'Sunita Sharma', relation: 'Mother', date: 'Oct 12' }, { name: 'Amit Sharma', relation: 'Brother', date: 'Sep 20' }, { name: 'Priya Sharma', relation: 'Sister', date: 'Aug 5' }], agreement: 'active' },
//   { id: 3, room: '105', tenant: 'Jatin B.',  leftMonth: null,           policeVerif: 'pending',  checkIns: [{ date: 'Oct 15', reason: 'Personal leave' }], visitors: [], agreement: 'expiring_soon' },
//   { id: 4, room: '201', tenant: 'Divya J.',  leftMonth: null,           policeVerif: 'verified', checkIns: [{ date: 'Sep 10', reason: 'Wedding' }, { date: 'Oct 5', reason: 'Medical' }, { date: 'Oct 18', reason: 'Holiday' }], visitors: [{ name: 'Neha Singh', relation: 'Friend', date: 'Oct 18' }, { name: 'Priya Mehta', relation: 'Friend', date: 'Sep 25' }, { name: 'Ravi Joshi', relation: 'Cousin', date: 'Aug 12' }, { name: 'Shweta Roy', relation: 'Colleague', date: 'Aug 1' }, { name: 'Ankit Dev', relation: 'Friend', date: 'Jul 20' }], agreement: 'active' },
//   { id: 7, room: '304', tenant: 'Rahul M.',  leftMonth: null,           policeVerif: 'rejected', checkIns: [{ date: 'Oct 25', reason: 'Room transfer' }], visitors: [{ name: 'Arjun Malhotra', relation: 'Brother', date: 'Oct 22' }], agreement: 'active' },
//   { id: 8, room: '102', tenant: 'Sneha K.',  leftMonth: null,           policeVerif: 'verified', checkIns: [{ date: 'Sep 5', reason: 'Festival' }], visitors: [{ name: 'Kavita K.', relation: 'Mother', date: 'Sep 15' }, { name: 'Raj K.', relation: 'Father', date: 'Aug 20' }], agreement: 'active' },
//   { id: 2, room: '302', tenant: 'Amit G.',   leftMonth: 'February 2026', policeVerif: 'verified', checkIns: [{ date: 'Jan 10', reason: 'Family visit' }, { date: 'Feb 2', reason: 'Festival' }], visitors: [{ name: 'Geeta G.', relation: 'Mother', date: 'Jan 20' }], agreement: 'expired' },
//   { id: 5, room: '201', tenant: 'Ravi P.',   leftMonth: 'February 2026', policeVerif: 'verified', checkIns: [{ date: 'Jan 15', reason: 'Medical' }, { date: 'Feb 10', reason: 'Personal' }], visitors: [], agreement: 'expired' },
//   { id: 6, room: '201', tenant: 'Sonal K.',  leftMonth: 'January 2026',  policeVerif: 'verified', checkIns: [{ date: 'Dec 20', reason: 'Holiday' }, { date: 'Jan 5', reason: 'Festival' }], visitors: [{ name: 'Priya K.', relation: 'Sister', date: 'Jan 10' }], agreement: 'expired' },
//   { id: 9, room: '102', tenant: 'Kiran D.',  leftMonth: 'January 2026',  policeVerif: 'verified', checkIns: [{ date: 'Dec 15', reason: 'Festival' }, { date: 'Jan 2', reason: 'Vacation' }], visitors: [{ name: 'Suresh D.', relation: 'Brother', date: 'Dec 28' }], agreement: 'expired' },
// ]

// const MONTH_ORDER = ['March 2026', 'February 2026', 'January 2026', 'December 2025']

// const POLICE_CFG = {
//   verified: { label: 'Verified',  color: '#16A34A', dot: '#22C55E' },
//   pending:  { label: 'Pending',   color: '#D97706', dot: '#F59E0B' },
//   rejected: { label: 'Rejected',  color: '#DC2626', dot: '#EF4444' },
// }

// const AGREEMENT_CFG = {
//   active:        { label: 'Active',        bg: '#F0FDF4', text: '#166534', border: '#86EFAC' },
//   expired:       { label: 'Expired',       bg: '#FFF1F2', text: '#991B1B', border: '#FECACA' },
//   expiring_soon: { label: 'Expiring Soon', bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
// }

// const PAGE_SIZE = 5

// // ─── Record Modal ─────────────────────────────────────────────────
// const RecordModal = ({ title, sub, rows, columns, onClose }) => (
//   <div className="oc-modal-overlay" >
//     <div className="oc-modal" onClick={e => e.stopPropagation()}>
//       <div className="oc-modal-head">
//         <div>
//           <h3 className="oc-modal-title">{title}</h3>
//           <p className="oc-modal-sub">{sub}</p>
//         </div>
//         <button className="oc-modal-close" onClick={onClose}>
//           <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
//           </svg>
//         </button>
//       </div>
//       <div className="oc-modal-body">
//         {rows.length === 0 ? (
//           <p className="oc-modal-empty">No records found.</p>
//         ) : (
//           <table className="oc-modal-table">
//             <thead>
//               <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
//             </thead>
//             <tbody>
//               {rows.map((row, i) => (
//                 <tr key={i}>{columns.map(c => <td key={c.key}>{row[c.key]}</td>)}</tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </div>
//     </div>
//   </div>
// )

// // ─── Compliance Record Row ────────────────────────────────────────
// const RecordRow = ({ r }) => {
//   const [modal, setModal] = useState(null)
//   const [showAgreement, setShowAgreement] = useState(false)
//   const ps = POLICE_CFG[r.policeVerif]
//   const as = AGREEMENT_CFG[r.agreement]

//   // Use the imported JSON data directly - no buildAgreementData needed
//   // The agreement data is now static from the JSON file

//   return (
//     <>
//       <div className="oc-rec-row">
//         <div className="oc-rc oc-rc-room">{r.room}</div>
//         <div className="oc-rc oc-rc-tenant"><strong>{r.tenant}</strong></div>
//         <div className="oc-rc oc-rc-reg">
//           <button className="oc-link-btn">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             View
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-police">
//           <span className="oc-police-label" style={{ color: ps.color }}>
//             <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
//               {r.policeVerif === 'verified'
//                 ? <path d="M2 6L5 9L10 3" stroke={ps.dot} strokeWidth="2" strokeLinecap="round"/>
//                 : <circle cx="6" cy="6" r="5" stroke={ps.dot} strokeWidth="1.5"/>}
//             </svg>
//             {ps.label}
//           </span>
//           <button className="oc-icon-btn" title="Download">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-checkin">
//           <button className="oc-link-btn" onClick={() => setModal('checkin')}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             {r.checkIns.length} record{r.checkIns.length !== 1 ? 's' : ''}
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-visitors">
//           <button className="oc-link-btn" onClick={() => setModal('visitors')}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//             {r.visitors.length} visitor{r.visitors.length !== 1 ? 's' : ''}
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-agreement">
//           <span className="oc-agreement-badge" style={{ background: as.bg, color: as.text, borderColor: as.border }}>{as.label}</span>
//           <button className="oc-icon-btn" title="View agreement" onClick={() => setShowAgreement(true)}>
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
//           </button>
//         </div>
//         <div className="oc-rc oc-rc-download">
//           <button className="oc-download-btn">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
//             Download All
//           </button>
//         </div>
//       </div>

//       {modal === 'checkin' && (
//         <RecordModal
//           title="Check-In / Check-Out Records"
//           sub={`${r.tenant} · Room ${r.room}`}
//           rows={r.checkIns}
//           columns={[{ key: 'date', label: 'Date' }, { key: 'reason', label: 'Reason' }]}
//           onClose={() => setModal(null)}
//         />
//       )}
//       {modal === 'visitors' && (
//         <RecordModal
//           title="Visitor Records"
//           sub={`${r.tenant} · Room ${r.room}`}
//           rows={r.visitors}
//           columns={[{ key: 'name', label: 'Visitor Name' }, { key: 'relation', label: 'Relation' }, { key: 'date', label: 'Date' }]}
//           onClose={() => setModal(null)}
//         />
//       )}

//       {showAgreement && (
//         <ViewAgreementModal
//           fileName={`Rent_Agreement_Room-${r.room}_${r.tenant.replace(/\s+/g, '_')}.pdf`}
//           onClose={() => setShowAgreement(false)}
//         />
//       )}
//     </>
//   )
// }

// // ─── Records Table Head ───────────────────────────────────────────
// const RecordsHead = () => (
//   <div className="oc-rec-head">
//     <div className="oc-rc oc-rc-room">Room</div>
//     <div className="oc-rc oc-rc-tenant">Tenant Name</div>
//     <div className="oc-rc oc-rc-reg">Tenant Register</div>
//     <div className="oc-rc oc-rc-police">Police Verif.</div>
//     <div className="oc-rc oc-rc-checkin">Check-In/Out</div>
//     <div className="oc-rc oc-rc-visitors">Visitors</div>
//     <div className="oc-rc oc-rc-agreement">Agreement</div>
//     <div className="oc-rc oc-rc-download">Download All</div>
//   </div>
// )

// // ─── Pending Requests Tab ─────────────────────────────────────────
// const PendingRequests = () => {
//   const [filter,   setFilter]   = useState('all')
//   const [page,     setPage]     = useState(1)
//   const [requests, setRequests] = useState(PENDING_REQUESTS)
//   const [pvList,   setPvList]   = useState(POLICE_VERIFICATION)

//   const filtered   = requests.filter(r => filter === 'all' || r.type === filter)
//   const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
//   const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

//   const approveReq   = id => setRequests(p => p.filter(r => r.id !== id))
//   const rejectReq    = id => setRequests(p => p.filter(r => r.id !== id))
//   const markVerified = id => setPvList(p => p.map(r => r.id === id ? { ...r, status: 'verified' } : r))

//   return (
//     <div className="oc-root">
//       <div className="oc-section">
//         <div className="oc-section-title-row">
//           <h3 className="oc-section-title">Pending Requests</h3>
//           <span className="oc-count-badge">{requests.length}</span>
//         </div>

//         <div className="oc-filter-pills">
//           {[['all','All', requests.length], ['visitor','Visitor', requests.filter(r=>r.type==='visitor').length], ['checkin','Check-In / Check-Out', requests.filter(r=>r.type==='checkin').length]].map(([key, label, count]) => (
//             <button key={key} className={`oc-filter-pill ${filter === key ? 'active' : ''}`}
//               onClick={() => { setFilter(key); setPage(1) }}>
//               {label}
//               {count > 0 && <span className="oc-pill-count">{count}</span>}
//             </button>
//           ))}
//         </div>

//         <div className="oc-table">
//           <div className="oc-table-head">
//             <div className="oc-col oc-col-room">Room</div>
//             <div className="oc-col oc-col-tenant">Tenant</div>
//             <div className="oc-col oc-col-type">Type</div>
//             <div className="oc-col oc-col-details">Details</div>
//             <div className="oc-col oc-col-date">Date Range</div>
//             <div className="oc-col oc-col-actions">Actions</div>
//           </div>
//           {paged.map(r => (
//             <div key={r.id} className="oc-table-row">
//               <div className="oc-col oc-col-room">{r.room}</div>
//               <div className="oc-col oc-col-tenant"><strong>{r.tenant}</strong></div>
//               <div className="oc-col oc-col-type">
//                 <span className={`oc-type-badge oc-type-${r.type}`}>
//                   {r.type === 'visitor'
//                     ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
//                     : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
//                   }
//                   {r.type === 'visitor' ? 'Visitor' : 'Check-In/Out'}
//                 </span>
//               </div>
//               <div className="oc-col oc-col-details">
//                 <span className="oc-detail-main">{r.details}</span>
//                 {r.person && <span className="oc-detail-sub">{r.person}</span>}
//               </div>
//               <div className="oc-col oc-col-date">{r.dateRange}</div>
//               <div className="oc-col oc-col-actions">
//                 <button className="oc-btn oc-btn-approve" onClick={() => approveReq(r.id)}>Approve</button>
//                 <button className="oc-btn-text-reject" onClick={() => rejectReq(r.id)}>Reject</button>
//               </div>
//             </div>
//           ))}
//         </div>

//         {totalPages > 1 && (
//           <div className="oc-pagination">
//             <span className="oc-page-info">Page {page} of {totalPages}</span>
//             <div className="oc-page-btns">
//               <button className="oc-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
//                 <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
//               </button>
//               <button className="oc-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
//                 <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
//               </button>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Police Verification */}
//       <div className="oc-section oc-police-verification">
//         <div className="oc-section-title-row">
//           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
//           <h3 className="oc-section-title">Police Verification Pending</h3>
//           <span className="oc-count-badge oc-count-amber">{pvList.filter(r => r.status !== 'verified').length}</span>
//         </div>
//         <div className="oc-table">
//           <div className="oc-table-head">
//             <div className="oc-col oc-col-room">Room</div>
//             <div className="oc-col oc-col-tenant">Tenant Name</div>
//             <div className="oc-col oc-col-pv-status">Status</div>
//             <div className="oc-col oc-col-actions">Actions</div>
//           </div>
//           {pvList.map(r => (
//             <div key={r.id} className="oc-table-row">
//               <div className="oc-col oc-col-room">{r.room}</div>
//               <div className="oc-col oc-col-tenant"><strong>{r.tenant}</strong></div>
//               <div className="oc-col oc-col-pv-status">
//                 <span className={`oc-pv-badge oc-pv-${r.status}`}>{r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>
//               </div>
//               <div className="oc-col oc-col-actions">
//                 {r.status !== 'verified' ? (
//                   <>
//                     <button className="oc-btn-reminder">
//                       <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
//                       Send Reminder
//                     </button>
//                     <button className="oc-btn oc-btn-approve" onClick={() => markVerified(r.id)}>
//                       <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
//                       Mark Verified
//                     </button>
//                   </>
//                 ) : (
//                   <span className="oc-verified-label">
//                     <svg width="13" height="13" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="#16A34A" strokeWidth="2" strokeLinecap="round"/></svg>
//                     Verified
//                   </span>
//                 )}
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   )
// }

// // ─── Tenant Records Tab ───────────────────────────────────────────
// const TenantRecords = () => {
//   const [search,       setSearch]       = useState({ mobile: '', room: '', tenant: '' })
//   const [dateFrom,     setDateFrom]     = useState('')
//   const [dateTo,       setDateTo]       = useState('')
//   const [statusFilter, setStatusFilter] = useState('all')
//   const [openMonths,   setOpenMonths]   = useState({})

//   const activeRecords   = COMPLIANCE_RECORDS.filter(r => r.leftMonth === null)
//   const previousRecords = COMPLIANCE_RECORDS.filter(r => r.leftMonth !== null)

//   const applySearch = records => records.filter(r => {
//     const ms = !search.mobile || r.tenant.toLowerCase().includes(search.mobile.toLowerCase())
//     const rs = !search.room   || r.room.includes(search.room)
//     const ts = !search.tenant || r.tenant.toLowerCase().includes(search.tenant.toLowerCase())
//     return ms && rs && ts
//   })

//   const filteredActive = applySearch(activeRecords)

//   const byMonth = {}
//   applySearch(previousRecords).forEach(r => {
//     if (!byMonth[r.leftMonth]) byMonth[r.leftMonth] = []
//     byMonth[r.leftMonth].push(r)
//   })
//   const sortedMonths = Object.keys(byMonth).sort((a, b) => MONTH_ORDER.indexOf(a) - MONTH_ORDER.indexOf(b))

//   const toggleMonth = m => setOpenMonths(p => ({ ...p, [m]: !p[m] }))

//   return (
//     <div className="oc-root">
//       {/* Filter bar */}
//       <div className="oc-filter-bar">
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Mobile</label>
//           <input className="oc-filter-input" placeholder="Search mobile..." value={search.mobile}
//             onChange={e => setSearch(p => ({ ...p, mobile: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Room</label>
//           <input className="oc-filter-input" placeholder="Room no." value={search.room}
//             onChange={e => setSearch(p => ({ ...p, room: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Tenant</label>
//           <input className="oc-filter-input" placeholder="Name..." value={search.tenant}
//             onChange={e => setSearch(p => ({ ...p, tenant: e.target.value }))} />
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">From</label>
//           <div className="oc-date-wrap">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg>
//             <input type="date" className="oc-filter-input oc-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">To</label>
//           <div className="oc-date-wrap">
//             <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
//               <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
//             </svg>
//             <input type="date" className="oc-filter-input oc-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
//           </div>
//         </div>
//         <div className="oc-filter-group">
//           <label className="oc-filter-label">Status</label>
//           <select className="oc-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
//             <option value="all">All</option>
//             <option value="active">Active</option>
//             <option value="previous">Previous</option>
//           </select>
//         </div>
//       </div>

//       {/* Active Tenants */}
//       <div className="oc-section">
//         <div className="oc-section-title-row">
//           <h3 className="oc-section-title">Active Tenants</h3>
//           <span className="oc-count-badge">{filteredActive.length}</span>
//         </div>
//         <div className="oc-records-wrap">
//           <RecordsHead />
//           {filteredActive.map(r => <RecordRow key={r.id} r={r} />)}
//           {filteredActive.length === 0 && <div className="oc-table-empty">No active tenants match your search.</div>}
//         </div>
//       </div>

//       {/* Previous Tenants — accordion by month */}
//       <div className="oc-section">
//         <div className="oc-section-title-row">
//           <h3 className="oc-section-title">Previous Tenants</h3>
//           <span className="oc-count-badge oc-count-muted">{applySearch(previousRecords).length}</span>
//         </div>
//         <div className="oc-accordion">
//           {sortedMonths.map(month => {
//             const items  = byMonth[month]
//             const isOpen = openMonths[month]
//             return (
//               <div key={month} className="oc-accordion-item">
//                 <button className="oc-accordion-head" onClick={() => toggleMonth(month)}>
//                   <div className="oc-accordion-title">
//                     <span className="oc-accordion-month">{month}</span>
//                     <span className="oc-accordion-count">{items.length} tenant{items.length !== 1 ? 's' : ''}</span>
//                   </div>
//                   <svg className={`oc-chevron ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
//                     <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
//                   </svg>
//                 </button>
//                 {isOpen && (
//                   <div className="oc-accordion-body">
//                     <RecordsHead />
//                     {items.map(r => <RecordRow key={r.id} r={r} />)}
//                   </div>
//                 )}
//               </div>
//             )
//           })}
//         </div>
//       </div>
//     </div>
//   )
// }

// // ─── Main ─────────────────────────────────────────────────────────
// const OwnerCompliance = ({ selectedPG }) => {
//   const [activeTab, setActiveTab] = useState('pending')
//   return (
//     <div className="ocomp-root">
//       <div className="ocomp-tabs">
//         <button className={`ocomp-tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>Pending Requests</button>
//         <button className={`ocomp-tab ${activeTab === 'records' ? 'active' : ''}`} onClick={() => setActiveTab('records')}>Tenant Compliance Records</button>
//       </div>
//       {activeTab === 'pending' && <PendingRequests />}
//       {activeTab === 'records' && <TenantRecords />}
//     </div>
//   )
// }

// export default OwnerCompliance